import React from 'react';
import CardTable from './Table';
import { clearOneTypeToast, toast, getSafeRandom, getLangCode, getFxExpression } from '@platform/api';
import { isValidType } from '@platform/template';
import { ROW_STATUS } from '../../utils';
import { createUpdateAfterBatchChange } from '../utils';
import GridTable from "../GridTable/Table";

const loginLanguage = getLangCode();

/**
 * 更新数据（无论是批量还是单条，删行和增行不需要）的时候需要同步更新侧拉、展开，是表单，调用updateFormXXX即可
 * 孙表没有侧拉、展开，其他与主表无异
 * relation: 各execWithXXX方法
 * share是单来源多表格，每次数据改变如果不需要dependences循环，都需要share循环
 *
 * meta:
 * containerrelation
 *  二维数组 第一维是多少行 第二维是每行页签的多来源页签数量
 *
 * gridrelation
 * 单来源
 * destBrowseAreaCode (字符串) : 代表展开行的模板的id  meta[id]就是表单的模板
 * destEditAreaCode (侧拉) ： 代表侧拉行的模板的id(多个),可以同时显示多个表单，同上meta[id]
 * tableRelation : 单来源多页签
 *
 * 从containerrelation取出每行第一个，然后到gridrelation找到单来源多页签的关系
 * 没有containerrelation时，就只有一行，看gridrelation[主id]就是几个页签
 *
 *
 * relation:
 * dependences是所有的表，包括单来源和多来源
 * expand是展开行的模板id
 * grand是孙表，空的，create时添加
 * home是每行的第一个页签 通过id找到第一个页签
 * share单来源共享数据
 * side: 侧拉的
 * tables: 纵向的第一个页签id
 * tabs是每行的所有页签id(单来源 + 多来源的)
 */

const generateKey = () => String(new Date().getTime()).slice(-5) + getSafeRandom().toString(12);

/**
 * 创建CardTable
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {Config} config TODO:
 * @returns {CardTableComponent}
 */
export function createCardTable(id, config = {}) {
    if (!id) { return null; }
    let relation = this.tableRelation?.[id],
        meta = this.state.meta,
        mainStore = getStore.call(this, id);
    let loadedMeta;
    if (meta) {
        loadedMeta = Object.keys(meta).length;
    }

    /** @detail 必须先调用use方法并且设置模板，否则不渲染 */
    if (!mainStore) {
        console.warn(`请先调用this.props.use.cardTable('${id}');`);
        return null;
    }

    if (!relation || !loadedMeta) { return null; }
    let _runContext = this.getQiankunContext();
    let {
            multiConfig,
            grandTableMainId,
            customTabContent,
            fixFooter = true,
            scrollContainer,
        } = config,
        configs = { [id]: config, ...multiConfig } || { [id]: config },
        sharedConfig = {},
        grandConfig = {};
    //新模式下，取模板配置的参数
    relation.dependence.forEach(tableId => {
        // 共享的话配置只取源数据的
        let mainId = relation.share[tableId][0] !== tableId ? relation.share[tableId][0] : tableId; //不相等说明此tableId共享数据源，且不是源数据
        let layoutData = this.getCompPropFromLayout(mainId);
        configs[mainId] = { ...layoutData, ...configs[mainId] };
        if (meta.layout && !meta[id].isCustomMeta) { // isCustomMeta是业务上自己传的，模板默认为undefined
            configs[mainId].showIndex = !layoutData.hiddenIndex;
        }
    });

    if (grandTableMainId && !this.tableRelation[grandTableMainId]) {
        this.tableRelation[grandTableMainId] = createRelation.call(this, this.state.meta, grandTableMainId);
        this.tableRelation[grandTableMainId].dependence.forEach(tableId => {
            // 孙表的需要手动设置一下模板
            getStore.call(this, tableId).setMeta(this.state.meta, tableId);
        });
    }

    Object.entries(configs).forEach(([id, config]) => {
        // 单来源共享一个config
        relation.share[id]?.forEach(shareId => {
            sharedConfig[shareId] = { scrollContainer, fixFooter, ...config, customTabContent, ...multiConfig?.[shareId] };
        });

        // 合并孙表config
        config.grandGroup?.forEach(({ grandTableId, grandTableConfig }) => {
            grandConfig[grandTableId] = grandTableConfig;
            if (!this.tableRelation[grandTableId]) { // 不在grandTableMainId对应的dependence里的孙表
                this.tableRelation[grandTableId] = createRelation.call(this, this.state.meta, grandTableId);
                this.tableRelation[grandTableId].dependence.forEach(tableId => {
                    getStore.call(this, tableId).setMeta(this.state.meta, tableId);
                });
            }
        });
    });

    configs = { ...configs, ...sharedConfig, ...grandConfig };

    return <CardTable
        className="card-table"
        id={id}
        configs={configs}
        relation={relation}
        stores={{
            // 孙表store
            ...Object.entries(configs).reduce((grandStore, [id, config]) => {
                if (config.grandGroup) {
                    relation.grand[id] = [];
                    config.grandGroup.forEach(({ grandTableId }) => {
                        if (this.cardTableDataSource[grandTableId]) {
                            grandStore[grandTableId] = this.cardTableDataSource[grandTableId];
                        } else {
                            grandStore[grandTableId] = setStore.call(this, grandTableId);
                            grandStore[grandTableId].setMeta(this.state.meta, grandTableId);
                        }

                        relation.grand[id].push(grandTableId);
                        grandStore[grandTableId].setRelation(relation);
                    });
                }
                return grandStore;
            }, {}),
            ...relation.dependence.reduce((stores, moduleId) => {
                stores[moduleId] = getStore.call(this, moduleId);
                stores[moduleId].setRelation(relation);

                let sideId = relation.side[moduleId],
                    expandId = relation.expand[moduleId];

                if (sideId) {
                    stores[sideId] = getStore.call(this, sideId);
                    stores[sideId].setRelation(relation);
                }
                if (expandId) {
                    stores[expandId] = getStore.call(this, expandId);
                    stores[expandId].setRelation(relation);
                }

                return stores;
            }, {}),
        }}
        _runContext={_runContext}
        pageScope={this}
    />;
}

/**
 * 创建小卡片表格
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {CardConfig} config TODO:
 * @returns {GridTableComponent}
 */
export function createSmallCard(id, config = {}) {
    let store = getStore.call(this, id) || setStore.call(this, id),
        tableStore = getStore.call(this, config.tableId);

    config = { ...config, theme: 'simple', gridType: 'small-card' };
    store?.setConfig(config);
    tableStore?.setGridTableId(id);

    if (!store || !this.state.meta) {
        return null;
    }

    return <GridTable
        {...config}
        config={config}
        id={id}
        key={id}
        store={store}
        pageScope={this}
        type='small'
        dataSource={this.cardTableDataSource}
    />;
}

/**
 * 创建大卡片表格
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {CardConfig} config TODO:
 * @returns {GridTableComponent}
 */
export function createLargeCard(id, config = {}) {
    let store = getStore.call(this, id) || setStore.call(this, id),
        tableStore = getStore.call(this, config.tableId);

    config = { ...config, theme: 'simple', gridType: 'large-card' };
    store?.setConfig(config);
    tableStore?.setGridTableId(id);

    if (!store || !this.state.meta) {
        return null;
    }

    return <GridTable
        {...config}
        config={config}
        id={id}
        key={id}
        store={store}
        pageScope={this}
        type='large'
        dataSource={this.cardTableDataSource}
    />;
}

/**
 * 设置布局方式
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {'vertical' | 'horizontal'} layout
 */
export function setLayout(id, layout) {
    let store = getStore.call(this, id);
    /** @detail 未使用的配置 */
    store?.setCache(['currentTab', 'layout'], layout === 'vertical' ? 'vertical' : 'horizontal');
}

/**
 * 设置表格数据
 * @memberof CardTable
 * @param {ModuleId} id - 未使用
 * @param {Object<ModuleId, TableData>} data
 */
export function setTabsTableData(id, data) {
    if (!data || typeof data !== 'object') { return; }

    Object.keys(data).forEach(tabId => {
        let tableData = data[tabId];
        if (!tableData) { return; }

        /**@detail 同步单来源多页签数据 */
        execWithShare.call(this, tabId, tableId => {
            let store = getStore.call(this, tableId),
                { crossPageSelect } = store?.getConfig() || {};

            tableData = data[tableId] ?? tableData;

            if (!store || !tableData || typeof tableData !== 'object') { return; }

            !this.isUpdatePage && store.getInstance().preventUpdate(true);
            try {
                store.setRows(tableData);
                store.setAllRowsInfo(tableData);
                store.setPaginationInfo(tableData?.pageInfo);
                store.setCache('base', tableData);
                /**@detail 没有配置跨页选时，清空选中行 */
                if (!crossPageSelect) {
                    store.setCheckedKeys();
                }
                // 表单更新
                updateFormData.call(this, tableId, store.getRows());

            } catch (e) {
                console.error(e);
            }
            !this.isUpdatePage && store.getInstance().preventUpdate(false);
            store.forceUpdate();
        });
    });

}

/**
 * 重置所有数据
 * @memberof CardTable
 * @param {ModuleId} id
 */
export function resetTabsTableData(id) {
    execWithDependence.call(this, id, tabId => {
        let store = getStore.call(this, tabId),
            oldData = store?.getCache('base') || { rows: [] };

        !this.isUpdatePage && store.getInstance().preventUpdate(true);

        try {
            store.setStatus('browse');
            store.setAllRowsInfo(oldData);
            store.setPaginationInfo(oldData?.pageInfo);
            store.setRows(oldData, { format: false });

            // 表单更新
            updateFormData.call(this, tabId, store.getRows());

            //孙表更新TODO:OK
            oldData.rows?.forEach(row => {
                let rowKey = row.key || row.rowid || row.rowId;
                execWithGrands.call(this, tabId, grandId => {
                    store.setGrandRows(rowKey, grandId, store.getCache(['grandTableData', rowKey, grandId]));
                });
            });
        } catch (e) {
            console.error(e);
        }

        !this.isUpdatePage && store.getInstance().preventUpdate(false);
        store.forceUpdate();
    });
}

/**
 * 获取所有表格数据
 * @memberof CardTable
 * @param {ModuleId} id
 * @returns {Object<ModuleId, { rows: TableRow[] }>}
 */
export function getTabsTableData(id) {
    let allData = {};
    execWithDependence.call(this, id, tabId => {
        let store = getStore.call(this, tabId);
        allData[tabId] = { rows: store.getRows() };
    });
    return allData;
}

/**
 * 设置多个主表状态
 * @memberof CardTable
 * @param {ModuleId} id - 未使用
 * @param {Object<ModuleId, "add" | "edit" | "browse">} status
 */
export function setTabsTableStatus(id, status = {}) {
    Object.entries(status).forEach(([tableId, status]) => {
        let store = getStore.call(this, tableId),
            oldStatus = store.getStatus();

        store.setStatus(status); // clearRequiredStatus方法做了update优化，此处不需要

        /** @detail 状态变更为浏览态时，清除提示(toast提示和必输验证提示) */
        if (oldStatus !== 'browse' && status == 'browse') {
            clearOneTypeToast('danger');
            clearRequiredStatus.call(this, tableId); //做了update优化，不能嵌套优化
        }
    });
}

/**
 * 设置页签显示隐藏
 * @memberof CardTable
 * @param {ModuleId} id - 未使用
 * @param {Object<ModuleId, boolean>} settings
 */
export function setTabsTableShow(id, settings) {
    // 第一个id貌似没啥用，因为第二个参数已经把需要设置的id传进来了
    Object.entries(settings).forEach(([moduleId, visible]) => {
        let store = getStore.call(this, moduleId);
        store.setAreaVisible(visible);
        store.getInstance().updateComponent('CardTableWithModal');
    });
}

/**
 * 设置页签激活
 * @memberof CardTable
 * @param {ModuleId} id - 未使用
 * @param {ModuleId} tableId 被激活的页签ID
 */
export function setTabsTableActive(id, tableId) {
    execWithHome.call(this, tableId, moduleId => {
        let store = getStore.call(this, moduleId);
        store?.setActiveTab(tableId);
    });
}

/**
 * 设置页签展开
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {Object<ModuleId, boolean>} settings
 */
export function setTabsTableExpand(id, settings = {}) {
    let relation = getRelation.call(this, id);
    if (!relation) { return; }

    Object.keys(settings).forEach(tableId => {
        execWithHome.call(this, tableId, moduleId => {
            let store = getStore.call(this, moduleId);
            /**@detail 激活页签 */
            store.setActiveTab(tableId);
            // 实际上在toggleCardTable只会循环一次，调用setTableExpand,总共会有两次forceUpdate，暂时不优化了
            /**@detail 展开页签 */
            toggleCardTable.call(this, moduleId, settings[tableId]);
        });
    });
}

/**
 * 获取所有激活页签
 * @memberof CardTable
 * @param {ModuleId} id
 * @returns {ModuleId[]}
 */
export function getTabsTableActive(id) {
    let allActive = [];
    execWithTables.call(this, id, moduleId => {
        let store = getStore.call(this, moduleId);
        allActive.push(store.getActiveTab() || moduleId);
    });
    return allActive;
}

/**
 * 检查表格必输项全不为空，如有未输项则提示
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {boolean} autoFocus - 2207未实现
 * @returns {boolean} true - 检查通过(必输项全部有值)
 */
export function checkAllTabsRequire(id, autoFocus = true) {
    let { containerrelation } = this.state.meta,
        ids = containerrelation[id] ? [].concat(...containerrelation[id]) : [id],
        toastMsg = [],
        locale;

    ids.forEach(tabId => {
        execWithShare.call(this, tabId, shareId => {
            let shareStore = getStore.call(this, shareId),
                shareTableName = shareStore?.getMeta()?.[shareId].name,
                validateInfo = shareStore?.validate();

            if (!shareStore || !validateInfo.size) { return; }
            !locale && (locale = shareStore.getLocale());

            toastMsg.push(`${shareTableName}(${locale['table008' /*页签*/]}) :`); //这个后缀与其他的不同

            validateInfo.forEach((colKeys, rowKey) => {
                let errLabels = [],
                    row = shareStore.getRowData(rowKey);

                [...colKeys].forEach(colKey => {
                    let label = shareStore.getColumnProps(colKey, 'label');

                    errLabels.push(label);
                });

                toastMsg.push(`${locale['table_first' /*第*/]}${row.dataIndex + 1}${locale['table_row' /*行*/]}${errLabels.join(', ')}`);//这个【行】后缀与其他的不同
            });
        });
    });

    /**@detail 提示toast */
    toastMsg.length && toast({
        content: `${locale['table_no_empty' /*以下字段不能为空*/]}`,
        color: 'danger',
        TextArr: [
            `${locale['table0022'] /*展开*/}`,
            `${locale['table0023'] /*收起*/}`,
            `${locale['table0024'] /*关闭*/}`,
        ],
        groupOperation: true,
        groupOperationMsg: toastMsg,
    });

    return toastMsg.length === 0;
}

/**
 * 按单元格保存旧值
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {RowKey} rowKey
 * @param {ColKey} colKey
 * @param {string | boolean} value
 */
export function saveChangedRowsOldValue(id, rowKey, colKey, value) {
    let store = getStore.call(this, id);
    /**@detail 未使用的配置 */
    store.setCache(['oldRows', rowKey, colKey], value);
}

/**
 * 获取单元格旧值
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {RowKey} rowKey
 * @param {ColKey} colKey
 * @returns {*}
 */
export function getChangedRowsOldValue(id, rowKey, colKey) {
    let store = getStore.call(this, id);
    /**@detail 未使用的配置 */
    return store.getCache(['oldRows', rowKey, colKey]);
}

/**
 * 删除单元格旧值
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {RowKey} rowKey
 * @param {ColKey} colKey
 */
export function delChangedRowsOldValue(id, rowKey, colKey) {
    let store = getStore.call(this, id);
    /**@detail 未使用的配置 */
    store.setCache(['oldRows', rowKey, colKey], null);
}

/**
 * 设置表格数据
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {TableData} data - 表格数据
 * @param {function} callback - 设置完成回调
 * @param {boolean} isCache - 是否缓存数据
 * @param {boolean} isTop - 2207未实现
 * @param {boolean} autoFocus - 是否自动聚焦到第一个单元格
 */
export function setTableData(id, data, callback, isCache = true, isTop = false, autoFocus = false) {
    if (!data || !getStore.call(this, id)) { return; }

    let relation = getRelation.call(this, id),
        mainStore = getStore.call(this, id),
        checkedKeys = mainStore.getCheckedKeys();

    !data.rows && (data.rows = []);

    execWithShare.call(this, id, tabId => {
        let store = getStore.call(this, tabId),
            { crossPageSelect, pkname } = store?.getConfig() || {},
            { allpks, alltss } = store?.getAllRowsInfo() || {},
            isAllChecked = store.isAllChecked();

        if (!store) { return; }

        !this.isUpdatePage && store.getInstance().preventUpdate(true);

        try {
            /** @detail 清除排序、筛选、行点击、行选中、行禁用、最后一次check状态、组合排序 */
            store.setSortInfo();
            store.setFilterInfo();
            store.setClickedRow();
            store.setSelectedCell();
            store.setCopiedCell(); //清空复制单元格
            store.setLastCheckedKey();
            store.setDisabledKeys();
            store.setGroupSortInfo();

            /** @detail cardTable不支持分页、allpks、alltss */
            // 不支持的属性，先保留
            if (data.allpks) {
                allpks = data.allPks;
            } else if (data.rows && data.rows.length === 0) {
                allpks = [];
            }
            store.setAllRowsInfo({ allpks, alltss });

            // 不支持的属性，先保留
            store.setPaginationInfo(data.pageInfo);

            /** @detail 设置表格数据 */
            store.setRows(data);
            // store.setCache('oldRows', null); oldRows 如有问题再放开

            if (!crossPageSelect || !pkname || !data.allpks?.length || !allpks?.length || data?.allpks?.length !== allpks?.length || data?.rows?.length === 0) {
                /** @detail 清空选中数据的条件，同时符合：1. 没配置跨页选 2. 传了allpks且长度与上次的不一样  3. rows为空 */
                store.setCheckedKeys();
            } else if (checkedKeys?.size) { // 恢复的情况： 1. 反选、全选、当前页三种情况，反选怎么判断不知道
                if (isAllChecked) {
                    /** @detail 恢复选中数据的条件：下拉菜单全选时  */
                    store.setCheckedKeys(data.allpks);
                }
            }

            updateFormData.call(this, tabId, store.getRows());

            if (isCache) {
                store.setCache('base', { rows: store.getRows() });
            }
        } catch (e) {
            console.error(e);
        }

        !this.isUpdatePage && store.getInstance().preventUpdate(false);
        store.forceUpdate();
    });

    if (
        autoFocus &&
        mainStore.getStatus() === 'browse' &&
        data.rows.length &&
        (getStore.call(this, relation.home[id]).getActiveTab() || relation.home[id]) === id
    ) {
        //在browse状态下 聚焦到第一行和可见的第一列的单元格
        (function autoFocus() {
            requestAnimationFrame(() => {
                // 聚焦第一个元素
                let leafColumnKeys = mainStore.getVisibleLeafColumnKeys().filter(
                    key => isValidType(mainStore.getColumnProps(key, 'itemtype')),
                );

                if (leafColumnKeys.length) {
                    mainStore.focusOnCell(mainStore.getRows()[0]?.key, leafColumnKeys[0]);
                } else {
                    // DOM还没出来（还没有meta的时候），继续轮询
                    autoFocus();
                }
            });
        })();
    }

    callback?.call?.(this, { ...this.props }, id, data);

    /** @detail 由于更新了行选中状态，所以会触发config.selectedChange回调 */
    let { selectedChange } = mainStore?.getConfig() || {};
    typeof selectedChange === 'function' && selectedChange({ ...this.props, ...this.output }, id, 0);
    this.controlRule.willRun('setTableData');
}

/**
 * 获取表格状态
 * @memberof CardTable
 * @param {ModuleId} id
 * @returns {"add" | "edit" | "browse"}
 */
export function getStatus(id) {
    let store = getStore.call(this, id);
    return store.getStatus();
}

/**
 * 清除所有表格校验状态
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {boolean} clearAll - 是否清空孙表校验数据，默认true
 */
export function clearRequiredStatus(id, clearAll = true) {
    execWithDependence.call(this, id, tabId => {
        let store = getStore.call(this, tabId);
        if (!store) { return; }
        !this.isUpdatePage && store.getInstance().preventUpdate(true);

        try {
            store.clearValidate();
            clearAll && execWithGrands.call(this, tabId, grandId => {
                let grandStore = getStore.call(this, grandId);
                grandStore?.clearValidate();
            });

        } catch (e) {
            console.error(e);
        }

        !this.isUpdatePage && store.getInstance().preventUpdate(false);
        store.forceUpdate();
    });
}

/**
 * 设置表格状态
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {"add" | "edit" | "browse"} status
 */
export function setStatus(id, status) {
    let mainStore = getStore.call(this, id);
    execWithShare.call(this, id, shareId => {
        let store = getStore.call(this, shareId);
        if (!status || !store) { return; }

        let oldStatus = store.getStatus();

        // 以下代码status === 'browse'时最多会刷新2次 暂不优化
        if (oldStatus !== 'browse' && status === 'browse') {
            /**@detail 切换至浏览态时清除提示toast和必输验证的提示 */
            clearOneTypeToast('danger');
            clearRequiredStatus.call(this, id);
        } else if (status === 'edit') {
            /**@detail 编辑态下清除展开状态 */
            store.setExpandedRowKeys(new Set());
        }

        if (status === 'browse') {
            /** @detail 浏览态清除缓存的批改数据 */
            store.setCache('oldBatchData', undefined);
        }

        store.setStatus(status);

    });

    /**@detail 表格状态改变，触发config.tableStatusChanged */
    let { tableStatusChanged } = mainStore?.getConfig() || {};
    tableStatusChanged?.(status);
}

/**
 * 获取所有行数据（包含删除状态行）
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {boolean} isDeepCopy - 返回值是否深拷贝数据
 * @returns {TableRow[]}
 */
export function getAllRows(id, isDeepCopy = false) {
    let store = getStore.call(this, id),
        rows = store?.getRows(),
        checkedKeys = store?.getCheckedKeys();

    if (!store) { return []; }

    rows = rows.map(row => {
        row.selected = checkedKeys.has(row.key || row.rowid);
        return row;
    });

    return isDeepCopy ? JSON.parse(JSON.stringify(rows)) : rows;
}

/**
 * 获取所有行数据
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {boolean} isDeepCopy - 返回值是否深拷贝数据
 * @param {boolean} isFilter - true时获取当前视图数据，false时获取非删除的数据，默认false
 * @returns {TableRow[]}
 */
export function getVisibleRows(id, isDeepCopy = false, isFilter = false) {
    let store = getStore.call(this, id);

    if (!store) { return []; }

    let rows = isFilter ? store.getVisibleRows() : store.getRows().filter(row => row.status != ROW_STATUS.DELETE);

    return isDeepCopy ? JSON.parse(JSON.stringify(rows)) : rows;
}

/**
 * 获取变化行信息
 * @memberof CardTable
 * @param {ModuleId} id
 * @returns {TableRow[]}
 */
export function getChangedRows(id) {
    let store = getStore.call(this, id);
    let rows = store.getRows();
    /** @detail 返回非原始态的数据 */
    return rows.filter(row => {
        return row.status !== ROW_STATUS.ORIGIN;
    }).map(row => {
        return { rowid: row.key, status: row.status, values: row.values };
    });
}

/**
 * 获取表格行数
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {boolean} isAllRows - true时获取全部行数，false时获取可见行数
 * @returns {TableRow[]}
 */
export function getNumberOfRows(id, isAllRows) {
    return isAllRows ? getAllRows.call(this, id)?.length : getVisibleRows.call(this, id)?.length;
}

/**
 * 获取表格数据
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {boolean} [isDeepCopy=true] - 是否进行深拷贝
 * @returns {{areaType: 'table', rows: TableRow[], areacode: null}}
 */
export function getAllData(id, flag = true) {
    /** @detail 获取rows时内部调用了getAllRows */
    let rows = getAllRows.call(this, id, flag),
        store = getStore.call(this, id),
        { allpks, alltss } = store.getAllRowsInfo();
    return {
        areaType: 'table',
        rows,
        areacode: null,
        allpks,
        alltss,
    };
}

/**
 * 切换行展开收起
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {TableData} record
 */
export function toggleRowView(id, record) {
    let store = getStore.call(this, id),
        expandedKeys = store.getExpandedRowKeys(),
        targetRowKey = record.rowid || record.key;

    if (expandedKeys.has(targetRowKey)) {
        expandedKeys.delete(targetRowKey);
    } else {
        expandedKeys.add(targetRowKey);
    }

    store.setExpandedRowKeys(expandedKeys);
}

/**
 * 设置过滤条件
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {{ logic: 'or' | 'and', conditions: Array<{ key: ColKey, value, string, logic: 'equal' | 'greaterThan' | 'lessThan' | 'notIncluded' | 'included' }> }} record
 */
export function setFilterInfo(id, info) {
    let store = getStore.call(this, id);
    store?.setFilterInfo(info);
}

/**
 * 获取过滤条件
 * @memberof CardTable
 * @param {ModuleId} id
 */
export function getFilterInfo(id) {
    let store = getStore.call(this, id);
    return store?.getFilterInfo();
}

/**
 * 在指定位置新增行
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {DataIndex} dataIndex
 * @param {TableRow|Promise|function} defaultValue - 行默认值。如果没有指定默认值，会取模板默认值
 * @param {boolean} autoFocus - 是否自动聚焦首个单元格
 * @param {function} callback - 增行回调，row => {}
 * @param {boolean} flag - 2207未实现 多表头标识，默认false
 * @param {boolean} isAutoAddRow - 2207未实现
 * @param {boolean} shouldForceUpdate - 2207未实现
 */
export function addRow(id, dataIndex, defaultValue, autoFocus = true, callback, flag = false, isAutoAddRow, shouldForceUpdate = true) {
    let store = getStore.call(this, id),
        rows = store.getVisibleRows(), // 这里取visibleRows，由于不可见数据的存在（假删行），会导致增行时dataIndex有误
        key = generateKey();

    (typeof dataIndex === 'undefined' || dataIndex === -1 || dataIndex > rows.length) && (dataIndex = rows.length);
    dataIndex < 0 && (dataIndex = 0);

    if (defaultValue instanceof Promise) {
        // 注意异步问题，业务调用本接口后会同步调用其他方法
        defaultValue.then(rowValues => {
            execWithShare.call(this, id, tabId => {
                let tabStore = getStore.call(this, tabId);
                if (!tabStore) { return; }

                tabStore.addRow(dataIndex, { key, values: rowValues }, { autoFocus });

                updateFormData.call(this, tabId, tabStore.getRows());
            });
            typeof callback === 'function' && callback(id, dataIndex, { rows: store.getVisibleRows() });
        });
    } else {
        let rowValues = typeof defaultValue === 'function' ? (defaultValue() ?? {}) : JSON.parse(JSON.stringify(defaultValue ?? {}));
        execWithShare.call(this, id, tabId => {
            let tabStore = getStore.call(this, tabId);
            if (!tabStore) { return; }

            tabStore.addRow(dataIndex, { key, values: rowValues }, { autoFocus });

            updateFormData.call(this, tabId, tabStore.getRows());
        });

        typeof callback === 'function' && callback(id, dataIndex, { rows: store.getVisibleRows() });
    }
}

/**
 * 根据rowId的删除行
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {RowKey[]} rowKeys
 * @param {function} callback - 删除完成回调
 * @param {boolean} isDeepDel - 是否真删，默认false
 */
export function delRowByRowId(id, rowKeys, callback, isDeepDel) {
    let mainStore = getStore.call(this, id),
        checkedKeys = mainStore?.getCheckedKeys();

    if (!mainStore) { return; }

    !Array.isArray(rowKeys) && (rowKeys = [rowKeys]);

    rowKeys.forEach(rowKey => checkedKeys.delete(rowKey));

    execWithShare.call(this, id, tabId => {
        let store = getStore.call(this, tabId);

        if (!store) { return; }

        store.setCheckedKeys(checkedKeys, { shouldUpdate: false });
        store.deleteRow(rowKeys, isDeepDel);
        store.clearValidate(rowKeys); //清除这一行的错误状态 本方法不保证一定更新

        isDeepDel && store.setCache('base', { rows: store.getRows() });
        updateFormData.call(this, tabId, mainStore.getRows());
    });
    callback?.(id, rowKeys);
}

/**
 * 根据index的删除行
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {DataIndex | DataIndex[]} dataIndexes
 * @param {function} callback
 * @param {boolean} isDeepDel - 是否真删，默认false
 */
export function delRowsByIndex(id, dataIndexes, callback, isDeepDel) {
    !Array.isArray(dataIndexes) && (dataIndexes = [dataIndexes]);

    execWithShare.call(this, id, tabId => {
        let store = getStore.call(this, tabId),
            rows = store?.getRows(),
            checkedKeys = store?.getCheckedKeys();

        if (!store) { return; }

        let rowKeys = [];
        dataIndexes.forEach(dataIndex => {
            if (rows[dataIndex]) {
                rowKeys.push(rows[dataIndex].key);
            }
        });

        rowKeys.forEach(rowKey => checkedKeys.delete(rowKey));

        store.setCheckedKeys(checkedKeys, { shouldUpdate: false });
        store.deleteRow(rowKeys, isDeepDel);

        isDeepDel && store.setCache('base', { rows: store.getRows() });

        updateFormData.call(this, tabId, store.getRows());

        callback?.(tabId, dataIndexes, undefined, rowKeys);

        // 清除验证状态
        store.clearValidate(rowKeys);
    });
}

/**
 * 根据dataIndex设置单元格的值
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {DataIndex} dataIndex
 * @param {ColKey} colKey
 * @param {CellValue} cellValue
 * @param {function} callback - 2207未实现
 * @param {boolean} isSetEdit - 2207未实现
 * @param {boolean} isUpdateBatch - 2207未实现
 */
export function setValByKeyAndIndex(
    id,
    dataIndex,
    colKey,
    cellValue,
    callback,
    isSetEdit = false,
    isUpdateBatch = false,
) {
    execWithShare.call(this, id, tabId => {
        let store = getStore.call(this, tabId),
            row = store?.getRows()?.[dataIndex];

        if (!store || !row) { return; }

        let rawCellValue = store.getCellValue(row.key, colKey);
        /** @detail 合并数据 */
        store.setCellValue(row.key, colKey, { value: { ...rawCellValue, ...cellValue }, shouldForceUpdate: false });

        updateFormItemData.call(this, tabId, row.dataIndex, colKey, cellValue);

        // store.forceUpdate();
    });
}

/**
 * 根据rowId设置单元格的值
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {RowKey} rowKey
 * @param {ColKey} colKey
 * @param {CellValue} cellValue
 * @param {function} callback - 2207未实现
 * @param {boolean} isSetEdit - 2207未实现
 * @param {boolean} isUpdateBatch - 2207未实现
 */
export function setValByKeyAndRowId(
    id,
    rowKey,
    colKey,
    cellValue,
    callback,
    isSetEdit = false,
    isUpdateBatch = true,
) {
    execWithShare.call(this, id, tabId => {
        let store = getStore.call(this, tabId);

        if (!store) { return; }

        let rawCellValue = store.getCellValue(rowKey, colKey);
        /** @detail 合并数据 */
        store.setCellValue(rowKey, colKey, { value: { ...rawCellValue, ...cellValue }, shouldForceUpdate: false });

        updateFormItemData.call(this, tabId, store.getRowData(rowKey)?.dataIndex, colKey, cellValue);
    });
}

/**
 * 取消编辑 TODO 看下是不是可以复用setTableData方法和set孙表的方法
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {function} callback - 2207未实现
 */
export function resetTableData(id, callback) {
    let store = getStore.call(this, id),
        oldStatus = store.getStatus(),
        oldData = store.getCache('base') || { rows: [] };

    !oldData.rows && (oldData.rows = []);

    /** @detail 清除提示 */
    clearOneTypeToast('danger');

    if (oldStatus === 'edit') {
        /** @detail 调用接口前是编辑态时清除必输验证提示 */
        clearRequiredStatus.call(this, id);
    }

    /** @detail 设置成浏览态 */
    store.setStatus('browse');
    execWithShare.call(this, id, tabId => {
        let tabStore = getStore.call(this, tabId);

        if (!tabStore) { return; }

        !this.isUpdatePage && tabStore.getInstance().preventUpdate(true);
        try {
            tabStore.setAllRowsInfo(oldData);
            tabStore.setPaginationInfo(oldData?.pageInfo);
            tabStore.setRows(oldData, { format: false });

            //表单
            updateFormData.call(this, tabId, tabStore.getRows());

            //孙表TODO:OK
            oldData.rows?.forEach(row => {
                let rowKey = row.key || row.rowid;
                execWithGrands.call(this, tabId, grandId => {
                    if (store.getCache(['grandTableData', rowKey, grandId])?.rows) {
                        store.setGrandRows(rowKey, grandId, store.getCache(['grandTableData', rowKey, grandId]));
                    }
                });
            });
        } catch (e) {
            console.error(e);
        }
        !this.isUpdatePage && tabStore.getInstance().preventUpdate(false);
        tabStore.forceUpdate();
    });
    this.controlRule.willRun('resetTableData');
}

/**
 * 更新表格数据，仅更新已有数据
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {{ rows: TableRow[] }} data
 * @param {function} callback - 2207未实现
 * @param {boolean} isCache
 */
export function updateTableData(id, data, callback, isCache = true) {
    if (typeof data !== 'object' || !Array.isArray(data.rows)) { return; }

    let store = getStore.call(this, id),
        rows = data.rows.filter(row => store.getRowData(row.rowid || row.rowId || row.key));

    execWithShare.call(this, id, tabId => {
        let tabStore = getStore.call(this, tabId);

        if (!tabStore) { return; }

        tabStore.setRowData(rows);
        isCache && tabStore.setCache('base', { rows: tabStore.getRows() });

        updateFormData.call(this, tabId, tabStore.getRows());
    });

    // rows.forEach(row => {
    //     Object.keys(row.values).forEach(colKey => {
    //         saveChangedRowsOldValue.call(this, id, row.key, colKey, row.values[colKey].value);
    //     });
    // }); // 暂不存旧值

    /** @detail 更新完毕调用config.selectedChange */
    let { selectedChange } = store?.getConfig() || {};
    typeof selectedChange === 'function' && selectedChange({ ...this.props, ...this.output }, id, 0, { selected: false });
    this.controlRule.willRun('setTableData');
}

/**
 * 根据dataIndex获取单元格的值
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {DataIndex} dataIndex
 * @param {ColKey} colKey
 * @param {boolean} flag - 2207未实现
 * @returns {CellValue}
 */
export function getValByKeyAndIndex(id, dataIndex, colKey, flag = false) {
    let store = getStore.call(this, id),
        row = store.getRows()?.[dataIndex];
    if (!row) { return {}; }

    /** @detail 复用getValByKeyAndRowId方法 */
    return getValByKeyAndRowId.call(this, id, row.key, colKey);
}

/**
 * 根据rowId获取单元格的值
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {RowKey}} rowKey
 * @param {ColKey} colKey
 * @param {boolean} flag - 2207未实现
 * @returns {CellValue}
 */
export function getValByKeyAndRowId(id, rowKey, colKey, flag = false) {
    let store = getStore.call(this, id);
    return store.getCellValue(rowKey, colKey) || {};
}

/**
 * 聚焦单元格
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {RowKey} rowKey
 * @param {ColKey} colKey
 */
export function focusOnCell(id, rowKey, colKey) {
    let store = getStore.call(this, id);
    store.focusOnCell(rowKey, colKey);
}

/**
 * 根据行ID设置单元格编辑性
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {RowKey | RowKey[]} rowKeys
 * @param {ColKey | ColKey[] | 0} colKeys 为0则代表设置所有可见列
 * @param {boolean} enable
 */
export function setEditableByRowId(id, rowKeys, colKeys, enable = false) {
    let store = getStore.call(this, id),
        relation = getRelation.call(this, id),
        sideRowKey = '', 
        open = false,
        isUpdateSideForm = false;

    if (relation) {
        let mainId = relation.main[id],
            mainStore = getStore.call(this, mainId);

        ({ rowKey: sideRowKey, open } = mainStore.getCardTableModalInfo());
    }

    !Array.isArray(rowKeys) && (rowKeys = [rowKeys]);
    if (colKeys === 0) {
        colKeys = store.getVisibleLeafColumnKeys();
    } else if (!Array.isArray(colKeys)) {
        colKeys = [colKeys];
    }

    rowKeys.forEach(rowKey => {
        colKeys.forEach(colKey => store.setCellProps(rowKey, colKey, { disabled: !enable }));
        if (open && !isUpdateSideForm && sideRowKey === rowKey) {
            isUpdateSideForm = true;
        }
    });

    if (!isUpdateSideForm) { return; }

    let sideStore = getSideFormStore.call(this, id);
    colKeys.forEach(colKey => {
        sideStore?.setItemAttribute?.(colKey, 'disabled', !enable);
    });
}

/**
 * 根据dataIndex设置单元格编辑性
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {DataIndex} dataIndexes
 * @param {ColKey | ColKey[]} colKeys
 * @param {boolean} enable
 */
export function setEditableByIndex(id, dataIndexes, colKeys, enable = false) {
    !Array.isArray(dataIndexes) && (dataIndexes = [dataIndexes]);
    !Array.isArray(colKeys) && (colKeys = [colKeys]);

    let store = getStore.call(this, id),
        rows = store.getRows(),
        rowKeys = dataIndexes.filter(dataIndex => dataIndex >= 0 && dataIndex < rows.length).map(dataIndex => rows[dataIndex].key);

    /** @detail 复用setEditableByRowId方法 */
    setEditableByRowId.call(this, id, rowKeys, colKeys, enable);
}

/**
 * 获取单元格属性
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {RowKey} rowKey
 * @param {ColKey} colKey
 * @param {string} attributeName
 */

export function getCellProps(id, rowKey, colKey, attributeName) {
    let store = getStore.call(this, id);
    return store.getCellProps(rowKey, colKey, attributeName);
}

/**
 * 获取列属性
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {ColKey} colKey
 * @param {string} attributeName
*/
export function getColumnProps(id, colKey, attributeName) {
    let store = getStore.call(this, id);
    return store.getColumnProps(colKey, attributeName);
}

/**
 * 根据行ID获取单元格编辑性
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {RowKey} rowKey
 * @param {ColKey} colKey
 */
export function getEditableByRowId(id, rowKey, colKey) {
    let store = getStore.call(this, id);

    return !store.getCellProps(rowKey, colKey, 'disabled');
}

/**
 *  粘贴行，复制指定dataIndex的行粘贴到该行的下一行
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {DataIndex} dataIndex - 要复制的行index
 * @param {ColKey[]} keys - 复制行数据中不粘贴的列
 */
export function pasteRow(id, dataIndex, keys = []) {
    let store = getStore.call(this, id),
        rows = store?.getRows();

    if (!store) { return; }

    if (dataIndex < 0 || dataIndex === undefined) {
        dataIndex = 0;
    } else if (dataIndex >= rows.length) {
        dataIndex = rows.length - 1;
    }

    let copyRow = rows[dataIndex],
        values = { ...copyRow.values },
        key = generateKey(),
        addedRow = { ...copyRow, values, key };

    keys.forEach(key => delete values[key]);

    execWithShare.call(this, id, tabId => {
        let tabStore = getStore.call(this, tabId);
        if (!tabStore) { return; }

        tabStore.addRow(copyRow.dataIndex + 1, addedRow);

        updateFormData.call(this, tabId, store.getRows());
    });
}

/**
 * 获取选中行
 * @memberof CardTable
 * @param {ModuleId} id
 * @returns {Array<{ data: TableRow, index: DataIndex, pageIndex: number }>}
 */
export function getCheckedRows(id) {
    let store = getStore.call(this, id),
        checkedKeys = store?.getCheckedKeys(),
        rows = store?.getRows();

    if (!checkedKeys?.size || !rows?.length || !store) { return []; }

    let { crossPageSelect, pkname } = store.getConfig(),
        isEditStatus = store.getStatus() === 'browse' ? false : true,
        pageInfo = store.getPaginationInfo(),
        allInfo = store.getAllRowsInfo(),
        allCheckedKeys = store?.getAllCheckedKeys(),
        checkedRows = [];

    /** @detail 跨页选且不是编辑态时获取的全部选中行数据 */
    if (crossPageSelect && pkname && !isEditStatus) {
        let currentPageRowKeys = new Map(rows.map(row => [row.values[pkname].value, row.key])),
            pkIndexes = {};

        allInfo.allpks.forEach((pk, index) => pkIndexes[pk] = index);

        [...allCheckedKeys].sort((pkA, pkB) => pkIndexes[pkA] - pkIndexes[pkB]).forEach(pk => {
            if (currentPageRowKeys.has(pk)) {
                let row = store.getRowData(currentPageRowKeys.get(pk));
                if (!row) { return; }

                checkedRows.push({
                    data: row,
                    index: row.dataIndex,
                    pageIndex: pageInfo?.pageIndex,
                });
            } else {
                let pkIndex = pkIndexes[pk];
                if (pkIndex === -1) { return; }
                checkedRows.push({
                    data: { pk: allInfo.allpks?.[pkIndex] || '', ts: allInfo.alltss?.[pkIndex] || '' },
                    index: pkIndex % pageInfo.pageSize,
                    pageIndex: Math.floor(pkIndex / pageInfo?.pageSize),
                });
            }
        });
    } else {
        /** @detail 非跨页选时获取的当前页选中行数据 */
        checkedKeys = new Set([...checkedKeys].sort((rowKeyA, rowKeyB) =>
            store.getRowData(rowKeyA) && store.getRowData(rowKeyB) ? store.getRowData(rowKeyA).viewIndex - store.getRowData(rowKeyB).viewIndex : 0,
        ));
        rows.forEach(row => {
            if (checkedKeys.has(row.key)) {
                checkedRows.push({ data: row, index: row.dataIndex });
            }
        });
    }
    return checkedRows;
}

/**
 * 全选/取消全选所有行
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {boolean} checked
 */
export function selectAllRows(id, checked) {
    execWithShare.call(this, id, tabId => {
        let store = getStore.call(this, tabId);
        if (checked) {
            store.checkAll();
        } else {
            store.setCheckedKeys();
            store.setClickedRow();
        }
    });

    let mainStore = getStore.call(this, id),
        { selectedChange } = mainStore.getConfig() || {};
    /** @detail 回调config.selectedChange */
    typeof selectedChange === 'function' && selectedChange({ ...this.props, ...this.output }, id, mainStore.getCheckedKeys()?.size || 0, { selected: true });
}

/**
 * 选中指定的dataIndex行
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {DataIndex | DataIndex[]} dataIndexes
 */
export function selectRowsByIndex(id, dataIndexes) {
    !Array.isArray(dataIndexes) && (dataIndexes = [dataIndexes]);

    let store = getStore.call(this, id),
        rows = store?.getRows(),
        { selectedChange } = store?.getConfig() || {};

    if (!rows?.length || !dataIndexes.length || !store) { return; }

    let keys = new Set(dataIndexes.map(dataIndex => rows[dataIndex].key));

    execWithShare.call(this, id, tabId => {
        let tabStore = getStore.call(this, tabId);

        if (!tabStore) { return; }

        tabStore.setCheckedKeys(keys);
    });

    /** @detail 回调config.selectedChange */
    typeof selectedChange === 'function' && selectedChange({ ...this.props, ...this.output }, id, keys.size, { selected: true });
}

/**
 * 取消选中指定的dataIndex行
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {DataIndex | DataIndex[]} dataIndexes
 */
export function unSelectRowsByIndex(id, dataIndexes) {
    !Array.isArray(dataIndexes) && (dataIndexes = [dataIndexes]);

    let store = getStore.call(this, id),
        rows = store.getRows(),
        checkedKeys = store.getCheckedKeys(),
        { selectedChange } = store.getConfig() || {};

    if (!rows?.length || !dataIndexes.length) { return; }

    // index对应的rowKey
    dataIndexes.forEach(dataIndex => checkedKeys.delete(rows[dataIndex].key));

    execWithShare.call(this, id, tabId => {
        let tabStore = getStore.call(this, tabId);

        if (!tabStore) { return; }

        tabStore.setCheckedKeys(checkedKeys);
    });

    /** @detail 回调config.selectedChange */
    typeof selectedChange === 'function' && selectedChange({ ...this.props, ...this.output }, id, store.getCheckedKeys()?.size || 0, { selected: false });
}

/**
 * 反选
 * @memberof CardTable
 * @param {ModuleId} id
 */
export function reverseSelected(id) {
    execWithShare.call(this, id, tableId => {
        let store = getStore.call(this, tableId);
        store?.checkReverse();
    });

    /** @detail 触发config.selectedChange */
    let moduleStore = getStore.call(this, id);
    let { selectedChange } = moduleStore?.getConfig() || {};
    typeof selectedChange === 'function' && selectedChange({ ...this.prop, ...this.output }, id, moduleStore.getCheckedKeys().size);
}

/**
 * 移除空行（从数据中真删）
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {ColKey[]} keys - 涉及到的列
 * @param {'except' | 'include'} rule - except：不包含的列；include：包含的列
 */
export function filterEmptyRows(id, keys, rule = 'except', isDeepDel) {
    !Array.isArray(keys) && (keys = [keys]);

    let store = getStore.call(this, id),
        rows = store.filterEmptyRows(keys, rule, isDeepDel);

    execWithShare.call(this, id, tabId => {
        let tabStore = getStore.call(this, tabId);

        if (!tabStore) { return; }

        !rows.length && tabStore.setCheckedKeys([], { shouldUpdate: false }); //清空选中信息 
        tabStore.setRows({ rows });
        updateFormData.call(this, tabId, tabStore.getRows());
    });
}

/**
 * 过滤孙表数据
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {Object<ModuleId, {keys: ColKey[], rule: string}>} grandTableConfig
 * @param {boolean} shouldForceUpdate - 2207未实现
 */
export function filterEmptyGrandRows(id, grandTableConfig, shouldForceUpdate = true) {
    let store = getStore.call(this, id),
        rows = store.getRows(),
        grandRows = store.getGrandRows();

    rows.forEach(row => {
        let rowKey = row.key || row.rowid;
        execWithGrands.call(this, id, grandId => {
            let currentGrandData = grandRows?.[rowKey]?.[grandId] || { rows: [] },
                keys = [],
                rule = 'except';

            if (grandTableConfig[grandId]) {
                if (Array.isArray(grandTableConfig[grandId].keys)) {
                    keys = grandTableConfig[grandId].keys;
                }
                if (grandTableConfig[grandId]?.rule) {
                    rule = grandTableConfig[grandId].rule;
                }
            }
            currentGrandData.rows = currentGrandData.rows.filter(row => {
                let values = row.values;
                let colKeys = new Set(Object.keys(values));

                /** @detail 不检查numberindex列 */
                colKeys.delete('numberindex');

                if (Array.isArray(keys)) {
                    if (rule == "except") {
                        keys.forEach(key => {
                            colKeys.delete(key);
                        });
                    } else if (rule == "include") {
                        let keysSet = new Set(keys);
                        colKeys = new Set([...colKeys].filter(key => keysSet.has(key)));
                    }
                }

                let isEmpty = [...colKeys].every(key => ['', null, undefined, false].includes(values[key].value));

                return !isEmpty;
            });
            store.setGrandRows(rowKey, grandId, currentGrandData);
        });
    });
}

/**
 * 获取非删除行的数据（不包含指定列）
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {ColKey[]} keys - 指定不包含的列ID
 * @returns {TableRow[]}
 */
export function getAllRowsRemoveKeys(id, keys) {
    !Array.isArray(keys) && (keys = [keys]);

    let store = getStore.call(this, id),
        rows = store.getRows();

    return rows.filter(row => row.status !== ROW_STATUS.DELETE).map(row => {
        keys.forEach(key => delete row.values[key]);
        return row;
    });
}

/**
 * 关闭所有展开行
 * @memberof CardTable
 * @param {ModuleId} id
 */
export function closeExpandedRow(id) {
    let store = getStore.call(this, id);
    store.setExpandedRowKeys(new Set());
}

/**
 * 获取缓存数据
 * @memberof CardTable
 * @param {ModuleId}  id
 */
export function getCacheData(id) {
    let store = getStore.call(this, id);
    return store.getCache('base');
}

/**
 * 设置焦点行
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {DataIndex} dataIndex
 */
export function focusRowByIndex(id, dataIndex) {
    let store = getStore.call(this, id),
        rowKey = store.getRows()[dataIndex].key;

    /** @detail 滚动到行 */
    store.scrollIntoView({ rowKey, colKey: store.getColumns()[0].key });
    /** @detail 背景色加深 */
    store.setClickedRow(rowKey);
}

/**
 * 获取模板
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {boolean} flag - 2207未实现
 * @returns {Meta} 获取的模板数据
 */
export function getMixinMetaData(id, flag = false) {
    let store = getStore.call(this, id);
    return store.getMeta();
}

/**
 * 过滤出满足条件的数据
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {ColKey[]} keys
 * @param {string} value
 * @returns {{ areaType: "table", rows: Array, areacode: null }} - 返回格式不对，这个方法估计没人用
 */
export function getFilterRows(id, keys, value, allRows) {
    let store = getStore.call(this, id),
        rows = store.getRows();

    if (typeof value === 'undefined' || value === '') {
        rows = rows.map(row => ({ ...row, filterIndex: row.dataIndex }));
        return {
            areaType: "table",
            rows: JSON.parse(JSON.stringify(rows)),
            areacode: null,
        };
    }

    !Array.isArray(keys) && (keys = [keys]);

    rows = rows.map(row => {
        if (row.status === ROW_STATUS.DELETE) { return row; }

        return {
            ...row,
            ...(keys.map(key => {
                let cellValue = store.getCellValue(row.key, key),
                    itemType = store.getColumnProps(key, 'itemtype');

                if (itemType === 'switch' || itemType === 'checkbox_switch' || itemType === 'switch_browse' || itemType === 'switch') {
                    value = typeof value === 'boolean' ? value : (value === 'Y' || value === 'true') ? true : false;
                    return cellValue?.value === value ? { filterIndex: row.dataIndex } : {};
                }

                return cellValue?._display_?.includes(value) ? { filterIndex: row.dataIndex } : {};
            })),
        };
    });

    return {
        areaType: "table",
        rows: [],
        areacode: null,
    };
}

/**
 * 设置筛选条件
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {ColKey[]} keys
 * @param {string} value - 筛选条件的值
 * @param {boolean} clearFilterCondition - 是否清空筛选条件。此时keys和value参数没用，默认false
 */
export function setFiltrateTableData(id, keys, value, clearFilterCondition) {
    let store = getStore.call(this, id),
        rows = store?.getRows();
    if (!store) { return; }

    !Array.isArray(keys) && (keys = [keys]);

    store.setFilterInfo(); //清空筛选条件

    if (!value || clearFilterCondition) {
        rows.forEach(row => delete row.filter);
    } else {
        rows.forEach(row => {
            if (row.status === ROW_STATUS.DELETE) { return false; }
            row.filter = true;

            for (let i = 0; i < keys.length; i++) {
                let colKey = keys[i],
                    { display, value: colValue } = row.values[colKey],
                    { itemtype: itemType, languageMeta } = store.getColumnProps(colKey);

                if (!row.filter) { break; }

                if (itemType === 'residtxt' && Array.isArray(languageMeta)) {
                    for (let i = 0; i < languageMeta.length; i++) {
                        let { index, languageCode } = languageMeta[i],
                            realCode = colKey;
                        if (languageCode === loginLanguage) {
                            // 登录语言值
                            realCode = index == 1 ? colKey : colKey + index;
                        }
                        let cellValue = row.values[realCode];
                        if (cellValue.display?.includes(value) || cellValue.value?.includes(value)) {
                            row.filter = false;
                            continue;
                        }
                    }
                } else {
                    if (typeof display === 'string' && display.includes(value)) {
                        row.filter = false;
                    }
                    if (typeof colValue === 'string' && colValue.includes(value)) {
                        row.filter = false;
                    }
                    if (typeof colValue === 'boolean') {
                        row.filter = display === value || colValue === value;
                    }
                }
            }
        });
    }
    store.setRows({ rows });
}

/**
 * 批量设置整列数据（要先选中某个单元格）
 * @memberof CardTable
 * @param {ModuleId} id
 * @returns {{ code: ColKey, value: Value, display: string }}
 */
export function batchChangeTableData(id) {
    let store = getStore.call(this, id),
        batchData = store.getCache('oldBatchData', { strict: true }),
        rows = store.getRows(),
        value = {};

    if (!batchData || !rows?.length) { return; }

    value = store.getCellValue(rows?.[batchData.dataIndex]?.key, batchData.colKey);

    rows = rows.map(row => row.status === ROW_STATUS.DELETE ? row : {
        ...row,
        status: row.status === ROW_STATUS.ORIGIN ? ROW_STATUS.EDIT : row.status,
        values: {
            ...row.values,
            [batchData.colKey]: { ...value },
        },
    });

    execWithShare.call(this, id, tabId => {
        let tabStore = getStore.call(this, tabId);
        if (!tabStore) { return; }

        tabStore.setRows({ rows });
        updateFormData.call(this, tabId, tabStore.getRows());
    });

    return {
        code: batchData.colKey,
        value: value.value,
        display: value.display,
    };
}

/**
 * 通过index更新行数据
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {Array<{ data: TableRow, index: DataIndex }>} records
 * @param {boolean} isCache
 * @param {boolean} autoFocus - 2207未实现
 */
export function updateDataByIndexs(id, records, isCache = false, autoFocus = false) {
    let store = getStore.call(this, id);

    !Array.isArray(records) && (records = [records]);

    let rows = [];

    records.forEach(({ data: row, index: dataIndex }) => {
        let rawRow = store.getRows()?.[dataIndex], values = {};

        if (!rawRow) { return; }

        Object.keys(row.values).forEach(colKey => {
            let { value, display, scale } = row.values[colKey];
            // 考虑undefined和null的情况，确保覆盖原值
            /** @detail 覆盖数据，scale无值时继承原有值 */
            values[colKey] = {
                value: value,
                display: display,
                scale: typeof scale === 'undefined' ? store.getCellValue(rawRow.key, colKey)?.scale : scale,
            };
        });

        rows.push({
            ...rawRow,
            ...row,
            values: { ...rawRow.values, ...values }, //避免values少属性
            status: (typeof row.status === 'undefined' || row.status == ROW_STATUS.ORIGIN) ? ROW_STATUS.EDIT : row.status,
        });
    });

    execWithShare.call(this, id, tabId => {
        let tabStore = getStore.call(this, tabId);

        if (!tabStore) { return; }

        tabStore.setRowData(rows);
        updateFormData.call(this, tabId, tabStore.getRows());

        isCache && tabStore.setCache('base', { rows: tabStore.getRows() });

        // 清除验证状态
        // tabStore.clearValidate();
    });
}

/**
 * 在指定位置插入行
 * @memberof CardTable
 * @param {ModuleId} moduleId
 * @param {Array<{ index: DataIndex, data: TableRow }>} record
 * @param {boolean} flag - 2207未实现
 * @param {boolean} autoFocus
 * @returns {{ rows: TableRow[] }}
 */
export function insertDataByIndexs(id, records, mergeInitialValue = false, autoFocus = false) {
    let store = getStore.call(this, id);

    if (!store) { return; }

    if (!Array.isArray(records) || !records.length) { return { rows: store?.getRows() ?? [] }; }

    let nextRows = records.map(({ index: dataIndex, data: row }) => {
        row = { ...row, key: generateKey() };
        return { dataIndex, row };
    });

    store.addRow(nextRows, { autoFocus, mergeInitialValue });

    let rows = store.getRows();
    execWithShare.call(this, id, tabId => {
        if (tabId !== id) {
            let shareStore = getStore.call(this, tabId);
            shareStore.setRows({ rows }, { format: false });
        }

        updateFormData.call(this, tabId, store.getRows());
    });

    return { rows };
}

/**
 * 根据index设置行状态
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {Array<{ index: DataIndex, data: TableRow }>} records
 */
export function setRowStatusByIndexs(id, records) {
    execWithShare.call(this, id, tabId => {
        let store = getStore.call(this, tabId),
            rows = store.getRows();

        Array.isArray(records) && records.forEach(({ index: dataIndex, status }) => {
            if (status === ROW_STATUS.DELETE) {
                delRowsByIndex.call(this, id, dataIndex);
            }
            rows[dataIndex].status = status;
        });

        store.forceUpdate();
    });
}

/**
 * 多行数据插入到index行后
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {TableRow | TableRow[]} addRows
 * @param {DataIndex} dataIndex
 */
export function insertRowsAfterIndex(id, addRows, dataIndex) {
    dataIndex = typeof dataIndex === 'undefined' ? 0 : dataIndex;

    let store = getStore.call(this, id),
        rows = store.getRows();

    !Array.isArray(addRows) && (addRows = [addRows]);

    if (!rows?.length || dataIndex < 0) {
        dataIndex = 0;
    } else if (dataIndex > rows.length) {
        dataIndex = rows.length;
    } else {
        dataIndex = dataIndex + 1;
    }

    addRows = addRows.map(row => ({ ...row, key: generateKey() }));
    store.addRow(dataIndex, addRows, { autoFocus: false });

    rows = store.getRows();
    execWithShare.call(this, id, tabId => {
        if (tabId !== id) {
            let shareStore = getStore.call(this, tabId);
            shareStore.setRows({ rows }, { format: false });
        }
        updateFormData.call(this, tabId, store.getRows());
    });
}

/**
 * 根据index设置单元格编辑性
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {DataIndex} dataIndex
 * @param {ColKey} colKey
 * @param {boolean} enable
 * @param {boolean} isUpdate - 2207未实现
 */
export function setTdEditByIndex(id, dataIndex, colKey, enable = true, isUpdate = true) {
    let store = getStore.call(this, id),
        rows = store.getRows();
    store.setCellProps(rows[dataIndex].key, colKey, { disabled: !enable });
}

/**
 * 根据index设置单元格值
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {DataIndex} dataIndex
 * @param {{ColKey: CellValue}} colValues
 */
export function setValByKeysAndIndex(id, dataIndex, colValues = {}) {
    let store = getStore.call(this, id),
        rows = store.getRows(),
        row = rows[dataIndex];

    for (let colKey in colValues) {
        row = { ...row, values: { ...row.values, [colKey]: { ...row.values[colKey], ...colValues[colKey] } } };
    }
    rows[dataIndex] = row;
    if (row.status === ROW_STATUS.ORIGIN) {
        row.status = ROW_STATUS.EDIT;
    }
    execWithShare.call(this, id, tabId => {
        let tabStore = getStore.call(this, tabId);

        if (!tabStore) { return; }

        tabStore.setRowData(row);
        updateFormData.call(this, tabId, rows);
    });
}

/**
 * 设置所有行复选框是否禁用
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {boolean} enable
 */
export function setAllCheckboxAble(id, enable = false) {
    execWithShare.call(this, id, tabId => {
        // checkbox禁用 但是要显示
        let store = getStore.call(this, tabId),
            rows = store.getRows();

        if (enable) {
            store.setDisabledKeys(new Set());
        } else {
            store.setDisabledKeys(rows.map(row => row.key));
        }
    });
}

/**
 * 设置搜索关键字
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {string} keyWord
 */
export function setKeyWord(id, keyWord) {
    execWithShare.call(this, id, tabId => {
        getStore.call(this, tabId)?.setKeyWord(keyWord);
    });
}

/**
 * 设置单元格属性
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {RowKey} rowKey
 * @param {ColKey} colKey
 * @param {Object} props
 */
export function setCellProps(id, rowKey, colKey, props) {
    let store = getStore.call(this, id);
    store.setCellProps(rowKey, colKey, props);
}

/**
 * 设置禁用行
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {Object<RowKey, boolean>} settings
 */
export function setDisabledRows(id, settings) {
    execWithShare.call(this, id, tabId => {
        let store = getStore.call(this, tabId),
            disabledKeys = store.getDisabledKeys();

        Object.entries(settings).forEach(([rowKey, value]) => {
            if (value) {
                disabledKeys.add(rowKey);
            } else {
                disabledKeys.delete(rowKey);
            }
        });

        store.setDisabledKeys(disabledKeys);
    });

}

/**
 * 设置多个表格数据
 * @memberof CardTable
 * @param {Object<ModuleId, TableData>} data
 * @param {function} callback () => {}
 */
export function setMulTablesData(data = {}, callback) {
    for (let id in data) {
        execWithShare.call(this, id, tabId => {
            let store = getStore.call(this, tabId);

            if (!store) { return; }

            store.setRows(data[id]);
            store.setCache('base', store.getRows());
            //表单
            updateFormData.call(this, tabId, store.getRows());
        });
        // 对假删数据的处理，这里暂时不做这个操作
        callback?.();
    }
}

/**
 * 根据index获取行数据
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {DataIndex | DataIndex[]} dataIndexes
 * @returns {TableRow[]}
 */
export function getRowsByIndexs(id, dataIndexes) {
    let store = getStore.call(this, id),
        rows = store.getRows();

    !Array.isArray(dataIndexes) && (dataIndexes = [dataIndexes]);

    return dataIndexes.map(dataIndex => rows[dataIndex]).filter(e => e);
}

/**
 * 获取表格的必输验证 不建议使用
 * @memberof CardTable
 * @param {ModuleId[]} ids - 指定检查的表格ID，不传本参数即代表检查所有表格
 * @param {boolean} flag - 2207未实现
 * @returns {object} 错误信息
 */
export function getAllTableRequire(ids, flag = false) {
    const errorInfo = {
        key: {},
        label: {},
        ids: [],
        validateFailed: {},
        firstValidateFaild: {},
    };

    ids && !Array.isArray(ids) && (ids = [ids]);

    let { gridrelation } = this.state.meta;

    for (let tabId in gridrelation) {
        let store = getStore.call(this, tabId),
            { tabRelation = [] } = gridrelation[tabId],
            tableName = store?.getMeta()?.[tabId].name || '空表';

        if (!store) { continue; }

        // 初始化当前表格的对象
        errorInfo.key[tabId] = {};
        errorInfo.label[`表_${tableName} > `] = {};
        errorInfo.validateFailed[tabId] = {};
        errorInfo.firstValidateFaild[tabId] = {};
        if (!ids || ids.includes(tabId)) {
            errorInfo.ids.push(tableName);
        }

        tabRelation.length && tabRelation.forEach(tableId => {
            //初始化
            errorInfo.key[tabId][`${tableId}_TAB`] = {};
            errorInfo.label[`表_${tableName} > `][`${tableName}(页签) ： `] = {};
            errorInfo.validateFailed[tabId][tableId] = {};

            let validateInfo = store.validate();
            validateInfo.forEach((colKeys, rowKey) => {
                let row = store.getRowData(rowKey);

                //初始化
                errorInfo.key[tabId][`${tableId}_TAB`][row.dataIndex] = {};
                errorInfo.label[`表_${tableName} > `][`${tableName}(页签) ： `][`第${row.dataIndex + 1}行：`] = {};
                errorInfo.validateFailed[tabId][tableId][rowKey] = {};

                [...colKeys].forEach(colKey => {
                    let label = store.getColumnProps(colKey, 'label');
                    errorInfo.key[tabId][`${tableId}_TAB`][row.dataIndex][colKey] = label;
                    errorInfo.label[`表_${tableName} > `][`${tableName}(页签) ： `][`第${row.dataIndex + 1}行：`][label] = ' 为空';
                    errorInfo.validateFailed[tabId][tableId][rowKey][colKey] = colKey;

                    // 第一行
                    if (!errorInfo.firstValidateFaild[tabId].length) {
                        errorInfo.firstValidateFaild[tabId] = [tableId, rowKey, colKey];
                    }
                });
            });

        });
    }
    return errorInfo;
}

/**
 * 获取最后一个操作过的单元格的信息（和batchChangeTableData配合）
 * @memberof CardTable
 * @param {ModuleId} id
 * @returns {{ batchChangeIndex: DataIndex, batchChangeKey: ColKey, batchChangeValue: Value, batchChangeDisplay: string }}
 */
export function getTableItemData(id) {
    let store = getStore.call(this, id),
        batchData = store.getCache('oldBatchData') || {},
        { colKey, dataIndex } = batchData,
        rows = store.getRows(),
        rowKey = rows?.[dataIndex]?.key,
        itemType = colKey && store.getColumnProps(colKey, 'itemtype'),
        cellValue = rowKey && colKey && store.getCellValue(rowKey, colKey);

    if (!rowKey) {
        return { batchChangeKey: '' };
    }

    return {
        batchChangeIndex: dataIndex,
        batchChangeKey: colKey,
        batchChangeValue: cellValue?.value,
        batchChangeDisplay: itemType === 'residtxt' ? null : cellValue?.display,
    };
}

/**
 * 设置某些表格某些列的精度
 * @memberof CardTable
 * @param {Array<{ areacode: ModuleId, fieldcode: ColKey, scale:number }>} settings
 */
export function setColScale(settings) {
    settings.forEach(setting => {
        const { areacode: id, fieldcode: colKey, scale } = setting;

        let store = getStore.call(this, id);

        store.setColumnProps(colKey, { scale });
    });
}

/**
 * 获取某列显隐性
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {ColKey} colKey
 * @returns {boolean}
 */
export function checkVisible(id, colKey) {
    let store = getStore.call(this, id);
    return store.getColumnProps(colKey, 'visible');
}

/**
 * 获取孙表的必输性验证信息
 * @memberof CardTable
 * @param {Object<ModuleId, ModuleId[]>} parentIds
 * @param {string} childRowKey - 2207未使用
 * @returns {Array<{id: ModuleId, info: string, row: Array<{index: number, rowKeyValue: RowKey, info: string, errorInfo: object}>}>}
 */
export function getChildTableRequired(parentIds = {}, childRowKey = 'rowid') {
    if (!parentIds) { return []; }

    let allErrorInfo = [],
        meta = this.state.meta,
        { gridrelation } = meta,
        json;

    Object.entries(parentIds).forEach(([parentId, grandIds]) => {
        let store = getStore.call(this, parentId),
            rows = store.getRows().filter(item => item.status != ROW_STATUS.DELETE),
            grandData = store.getGrandRows(),
            parentErrorInfo = [];

        if (!json) {
            json = store.getLocale();
        }

        rows.forEach((row, index) => {
            let rowKey = row.key || row.rowid,
                errorInfo = {
                    key: {},
                    label: {},
                    ids: [],
                },
                validateFailed = {},
                firstValidateFaild = {};

            grandIds.forEach(tableId => {
                let grandStore = getStore.call(this, tableId);
                const idInfo = gridrelation[tableId] || {};
                errorInfo.key[tableId] = {};
                const tableName = meta[tableId]['name'] || json['table0010'];

                //  校验
                validateFailed[tableId] = validateFailed[tableId] || {};
                firstValidateFaild[tableId] = firstValidateFaild[tableId] || {};

                const childRowKey = 'rowid';

                errorInfo.ids.push(tableName);

                const tableLabel = `${json['table007']}_` + tableName + ' > ';
                errorInfo.label[tableLabel] = {};
                // 筛选之前的代码
                const rows = grandData?.[rowKey]?.[tableId]?.rows || [];

                const tableDataRows = rows.filter(item => item.status != ROW_STATUS.DELETE);

                // destEditAreaCode是侧拉项  tabRelation为页签项
                const { tabRelation = [tableId] } = idInfo;

                // 共享页签
                if (tabRelation.length) {
                    tabRelation.forEach(idMain => {
                        const tabId = idMain + '_TAB';
                        const tabName = meta[idMain]['name'] || json['table009'];
                        const tabLabel = `${tabName}(${json['table008']})` + ' ： ';
                        errorInfo.key[tableId][tabId] = {};
                        errorInfo.label[tableLabel][tabLabel] = {};

                        validateFailed[tableId][idMain] = {};

                        let itemsMain = grandStore.getVisibleLeafColumnKeys();

                        itemsMain.forEach(colKey => {
                            let item = grandStore.getColumnProps(colKey);
                            if (!!item.required && !!item.visible && tableDataRows.length > 0) {
                                tableDataRows.forEach((val, index) => {
                                    const cellLabel = `${json['table_first']}${index + 1}${json['table_row']}`;
                                    errorInfo.key[tableId][tabId][index] = errorInfo.key[tableId][tabId][index] || {};
                                    errorInfo.label[tableLabel][tabLabel][cellLabel] = errorInfo.label[tableLabel][tabLabel][cellLabel] || {};

                                    if (val.values[item.attrcode]) {
                                        const checkedValue = val.values[item.attrcode].value;
                                        if (['', null, undefined].includes(checkedValue)) {
                                            errorInfo.key[tableId][tabId][index][item.attrcode] = item.label;
                                            errorInfo.label[tableLabel][tabLabel][cellLabel][item.label] = ' 为空';

                                            // 有就是有没有就是没有  给页签上判断用
                                            validateFailed[tableId][idMain][val[childRowKey]] = validateFailed[tableId][idMain][val[childRowKey]] || {};
                                            validateFailed[tableId][idMain][val[childRowKey]][item.attrcode] = item.attrcode;
                                            if (!Object.keys(firstValidateFaild[tableId]).length) {
                                                firstValidateFaild[tableId][idMain] = {};
                                                firstValidateFaild[tableId][idMain][val[childRowKey]] = item.attrcode;
                                            }
                                        }
                                    } else {
                                        errorInfo.key[tableId][tabId][index][item.attrcode] = item.label;
                                        errorInfo.label[tableLabel][tabLabel][cellLabel][item.label] = ' 为空';

                                        // 有就是有没有就是没有  给页签上判断用
                                        validateFailed[tableId][idMain][val[childRowKey]] = validateFailed[tableId][idMain][val[childRowKey]] || {};
                                        validateFailed[tableId][idMain][val[childRowKey]][item.attrcode] = item.attrcode;
                                        if (!Object.keys(firstValidateFaild[tableId]).length) {
                                            firstValidateFaild[tableId][idMain] = {};
                                            firstValidateFaild[tableId][idMain][val[childRowKey]] = item.attrcode;
                                        }
                                    }
                                });
                            }
                        });
                    });
                }
            });

            errorInfo.validateFailed = validateFailed;
            errorInfo.firstValidateFaild = firstValidateFaild;

            // 行信息
            parentErrorInfo.push({
                index,
                rowKeyValue: rowKey,
                info: `${json['table_first']}${index + 1}${json['table_row']}`,
                errorInfo,
            });
        });

        // 表信息
        allErrorInfo.push({
            id: parentId,
            info: meta[parentId]['name'] || json['table0010'],
            row: parentErrorInfo,
        });
    });

    return allErrorInfo;
}

/**
 * 获取必输性验证的提示信息
 * @memberof CardTable
 * @param {{ModuleId: ModuleId[]}} parentIds
 * @param {boolean} isReturnBool - 2207未使用
 * @param {string} childRowKey - 2207未使用
 * @returns {Array<string> | ''}
 */
export function getCheckChildTableRequiredToast(parentIds = {}, isReturnBool = true, childRowKey = 'rowid') {
    let allErrorInfo = getChildTableRequired.call(this, parentIds, childRowKey),
        json = getStore.call(this, Object.keys(parentIds)?.[0]).getLocale(),
        isRequired = false,
        toastMessage = [],
        requiredRows = { isNoRequired: false };

    // 校验信息
    allErrorInfo.forEach(rowErrorInfo => {
        requiredRows[rowErrorInfo.id] = {};
        if (rowErrorInfo.row) {
            // 缓存记录
            let grandValidateFailed = {};
            rowErrorInfo.row.forEach(rowInfo => {
                const parentLabel = rowErrorInfo.info + rowInfo.info;

                // 父表
                toastMessage.push('>>> ' + parentLabel);

                const { label, ids, validateFailed = {} } = rowInfo.errorInfo;
                // 记录
                grandValidateFailed[rowInfo.rowKeyValue] = validateFailed;
                // 被检验住的行
                const childRequiredRows = { isNoRequired: false };
                let messageWrong = []; //需要校验的表格
                messageWrong = Object.keys(label).filter(item =>
                    ids.some(item1 => item.startsWith(`${json['table007']}_${item1}`)),
                );
                messageWrong.forEach(eve => {
                    const labelItem = label[eve];
                    childRequiredRows[eve] = {};
                    Object.keys(labelItem).forEach(key => {
                        childRequiredRows[eve][key] = { childRequiredRows: [] };
                        const labelItemChild = labelItem[key];
                        let label = '';
                        if (
                            Object.keys(labelItemChild).length > 0 &&
                            Object.keys(labelItemChild).filter(key => {
                                return Object.keys(labelItemChild[key]).length;
                            }).length
                        ) {
                            label = key;
                        }
                        toastMessage.push(label);
                        Object.keys(labelItemChild).forEach((cont, index) => {
                            if (Object.keys(labelItemChild[cont]).length > 0) {
                                childRequiredRows[eve][key]['childRequiredRows'].push(index);
                                isRequired = true;
                                //对输出的校验内容进行格式转化
                                let str =
                                    cont +
                                    `${Object.keys(labelItemChild[cont])
                                        .map(ele => {
                                            return `[${ele}]`;
                                        })
                                        .join('，')}；`;
                                toastMessage.push(str);
                            }
                        });
                    });
                });

                // 记录结果
                requiredRows[rowErrorInfo.id][rowInfo.index] = childRequiredRows;
            });
        }
    });

    return isRequired ? toastMessage.filter(message => message) : '';
}

/**
 * 获取子表必输性验证的提示信息并提示
 * @memberof CardTable
 * @param {{ModuleId: ModuleId[]}} parentIds
 * @param {boolean} isReturnBool - 是否返回boolean类型，false返回对象，默认true
 * @param {boolean} childRowKey - 2207未使用
 * @returns {boolean | object}
 */
export function checkChildTableRequired(parentIds = {}, isReturnBool = true, childRowKey = 'rowid') {
    let allErrorInfo = getChildTableRequired.call(this, parentIds, childRowKey),
        json = getStore.call(this, Object.keys(parentIds)?.[0]).getLocale(),
        isRequired = false,
        toastMessage = [],
        requiredRows = { isNoRequired: false };

    // 校验信息
    allErrorInfo.forEach(rowErrorInfo => {
        requiredRows[rowErrorInfo.id] = {};
        if (rowErrorInfo.row) {
            // 缓存记录
            let grandValidateFailed = {};
            rowErrorInfo.row.forEach(rowInfo => {
                const parentLabel = rowErrorInfo.info + rowInfo.info;

                // 父表
                toastMessage.push('>>> ' + parentLabel);

                const { label, ids, validateFailed = {} } = rowInfo.errorInfo;
                // 记录
                grandValidateFailed[rowInfo.rowKeyValue] = validateFailed;
                // 被检验住的行
                const childRequiredRows = { isNoRequired: false };
                let messageWrong = []; //需要校验的表格
                messageWrong = Object.keys(label).filter(item =>
                    ids.some(item1 => item.startsWith(`${json['table007']}_${item1}`)),
                );
                messageWrong.forEach(eve => {
                    const labelItem = label[eve];
                    childRequiredRows[eve] = {};
                    Object.keys(labelItem).forEach(key => {
                        childRequiredRows[eve][key] = { childRequiredRows: [] };
                        const labelItemChild = labelItem[key];
                        let label = '';
                        if (
                            Object.keys(labelItemChild).length > 0 &&
                            Object.keys(labelItemChild).filter(key => {
                                return Object.keys(labelItemChild[key]).length;
                            }).length
                        ) {
                            label = key;
                        }
                        toastMessage.push(label);
                        Object.keys(labelItemChild).forEach((cont, index) => {
                            if (Object.keys(labelItemChild[cont]).length > 0) {
                                childRequiredRows[eve][key]['childRequiredRows'].push(index);
                                isRequired = true;
                                //对输出的校验内容进行格式转化
                                let str =
                                    cont +
                                    `${Object.keys(labelItemChild[cont])
                                        .map(ele => {
                                            return `[${ele}]`;
                                        })
                                        .join('，')}；`;
                                toastMessage.push(str);
                            }
                        });
                    });
                });

                // 记录结果
                requiredRows[rowErrorInfo.id][rowInfo.index] = childRequiredRows;
            });
        }
    });

    // 如果没通过
    if (isRequired) {
        toast({
            content: json['table_no_empty'],
            color: 'danger',
            groupOperation: true,
            isNode: true,
            TextArr: [json['table0022'], json['table0023'], json['table0024']],
            groupOperationMsg: toastMessage.filter(message => {
                return message;
            }),
            // mark: 'card-table-toastx',
        });
        return isReturnBool ? !isReturnBool : requiredRows;
    }

    return true;
}

/**
 * 检查表格必输验证并提示
 * @memberof CardTable
 * @param {ModuleId[]} tableIds
 * @param {boolean} flag - 2207未实现
 * @param {boolean} isReturnBool - 是否返回boolean类型，false返回对象，默认true
 * @param {boolean} autoFocus
 * @returns {boolean | object}
 */
export function checkTableRequired(tableIds, flag = false, isReturnBool = true, autoFocus = true) {
    let toastMessage = [],
        requiredRows = { isNoRequired: false },
        locale;

    !Array.isArray(tableIds) && (tableIds = [tableIds]);

    tableIds.forEach(tableId => {
        let store = getStore.call(this, tableId),
            firstErrorTab,
            firstErrorCell;
        if (!store) { return; }

        !locale && (locale = store.getLocale());
        let validateInfo = store.validate();
        if (!validateInfo.size) { return; }

        toastMessage.push(`${store.getMeta()?.[tableId].name}(${locale['table008' /*页签*/]}) ： `);

        validateInfo.forEach((colKeys, rowKey) => {
            let row = store.getRowData(rowKey),
                labels = [...colKeys].map(colKey => store.getColumnProps(colKey, 'label'));

            if (!firstErrorTab) {
                let [rowKey, colKeys] = [...validateInfo][0],
                    colKey = [...colKeys][0];

                firstErrorTab = tableId;
                firstErrorCell = [rowKey, colKey];
            }

            toastMessage.push(`${locale['table_first'/*第*/]}${row.viewIndex + 1}${locale['table_row'/*行*/]}`.concat(labels.map(label => `[${label}]`).join('，')).concat('；\n'));
        });

        if (autoFocus && firstErrorTab && firstErrorCell) {
            // 切换到第一个错误页签
            execWithHome.call(this, firstErrorTab, moduleId => {
                getStore.call(this, moduleId).setActiveTab(firstErrorTab);
            });

            // 等页签切完再聚焦
            setTimeout(() => {
                // 聚焦到第一个错误单元格
                getStore.call(this, firstErrorTab).focusOnCell(...firstErrorCell);
            }, 50);
        }

    });

    if (toastMessage.length === 0) { return true; }

    toast({
        content: `${locale['table_no_empty' /*以下字段不能为空*/]}`,
        color: 'danger',
        groupOperation: true,
        isNode: true,
        TextArr: [`${locale['table0022'] /*展开*/}`, `${locale['table0023'] /*收起*/}`, `${locale['table0024'] /*关闭*/}`],
        // groupOperationMsg: toastMessage.filter(message => message),
        groupOperationMsg: toastMessage,
    });

    return isReturnBool ? !isReturnBool : requiredRows;
}

/**
 * 设置表格数据
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {TableRow[]} rows
 * @returns {TableRow[]}
 */
export function setAllKeysVal(id, rows) {
    execWithShare.call(this, id, tabId => {
        let store = getStore.call(this, id);
        if (!store) { return; }

        store.setRowData(rows);
        updateFormData.call(this, tabId, store.getRows());
    });
    return rows;
}

/**
 * 设置点击行
 * @memberof CardTable
 * @param {ModuleId} moduleId
 * @param {{ index: DataIndex }} data
 */
export function setClickRowIndex(moduleId, data) {
    let store = getStore.call(this, moduleId),
        row = store.getRows()[data.index];

    store.setClickedRow(row.key);
}

/**
 * 获取点击行
 * @memberof CardTable
 * @param {ModuleId} id
 * @returns {{ record: TableRow, index: DataIndex }}
 */
export function getClickRowIndex(id) {
    let store = getStore.call(this, id),
        rowKey = store.getClickedRow(),
        row = store.getRowData(rowKey);

    if (!rowKey) { return; }

    return { record: row, index: row.dataIndex };
}

/**
 * 设置表格数据
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {TableData} data
 */
export function setSaveData(id, data) {
    execWithShare.call(this, id, tabId => {
        let store = getStore.call(this, tabId);
        if (!store) { return; }

        store.setRowData(data.rows, { shouldForceUpdate: false });
        store.setCache('base', { rows: data.rows });
        updateFormData.call(this, tabId, store.getRows());
    });
}

/**
 * 获取指定index的行数据
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {DataIndex} dataIndex
 * @returns {TableRow}
 */
export function getDataByIndex(id, dataIndex) {
    let store = getStore.call(this, id);
    return store.getRows()[dataIndex];
}

/**
 * 获取拖拽后的列宽
 * @memberof CardTable
 * @param {ModuleId} id
 * @returns {Object}
 */
export function getAllColumnsWidth(id) {
    let store = getStore.call(this, id);
    return store.getColumnsWidth();
}

/**
 * 更新行数据
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {{ rows: TableRow[] }} data
 * @param {boolean} isCache - 是否缓存，默认true
 * @param {boolean} isDel - 是否将删除的数据真删, 默认false
 */
export function updateDataByRowId(id, data, isDel = false, isCache = true) {
    let mainStore = getStore.call(this, id);

    if (data && data?.rows?.length) {
        let rows = data.rows.map(row => {
            let rowKey = row.rowid || row.rowId || row.key,
                rawRow = mainStore.getRowData(rowKey),
                values = {};

            if (!rawRow) { return; }

            Object.keys(row.values).forEach(colKey => {
                let { value, display, scale } = row.values[colKey];
                // 考虑undefined和null的情况，确保覆盖原值
                /** @detail 覆盖数据 */
                values[colKey] = {
                    value: value,
                    display: display,
                    scale: typeof scale === 'undefined' ? mainStore.getCellValue(rawRow.key, colKey)?.scale : scale,
                };
            });

            return {
                ...rawRow,
                ...row,
                status: typeof row.status === 'undefined' || row.status === ROW_STATUS.ORIGIN ? ROW_STATUS.EDIT : row.status,
                values: { ...rawRow.values, ...values }, //避免values少属性
            };

        }).filter(e => e);

        execWithShare.call(this, id, tabId => {
            let store = getStore.call(this, tabId);

            if (!store) { return; }

            store.setRowData(rows);
            updateFormData.call(this, tabId, store.getRows());

            let allRows = store.getRows();
            if (isDel) {
                store.setRows({ rows: allRows.filter(e => e.status !== ROW_STATUS.DELETE) });
                allRows = store.getRows();
            }

            if (isCache) {
                store.setCache('base', {
                    rows: allRows,
                    ...store.getAllRowsInfo(),
                    ...store.getPaginationInfo(),
                });
            }
        });
    }
    return { rows: mainStore?.getRows() || [] };
}

/**
 * 获取行的状态
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {DataIndex} dataIndex
 * @returns {"0" | "1" | "2" | "3"}
 */
export function getRowStatus(id, dataIndex) {
    let store = getStore.call(this, id),
        rowKey = store.getRows()[dataIndex].key;

    return store.getRowStatus(rowKey);
}

/**
 * 获得当前行的index
 * @memberof CardTable
 * @param {ModuleId} id
 * @returns {DataIndex}
 */
export function getCurrentIndex(id) {
    let store = getStore.call(this, id);

    return store.getRowData(store.getClickedRow())?.dataIndex;
}

/**
 * 差异更新,必须基于全量数据更新
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {TableData} data
 * @param {boolean} isCache - 2207未实现
 * @param {boolean} isDel 是否合并删除状态行，默认true
 * @param {boolean} isAddStatus 是否增加原来不存在的行，默认true
 * @returns {{rows: TableRow[], checkInfo: object}}
 */
// [郭祉祺加]
export function updateDiffDataByRowId(id, data, isCache = false, isDel = true, isAddStatus = true) {
    let store = getStore.call(this, id);

    let rows = store.getRows(),
        deletedRow = rows.filter(row => row.status == ROW_STATUS.DELETE);

    data.rows = data.rows.map(row => {
        let rowKey = row.key || row.rowid || row.rowId || generateKey();
        let currentRow = store.getRowData(rowKey);

        row.rowid = rowKey;

        if (currentRow) {// 只改了某个字段的值
            row.status = typeof row.status === 'undefined' || row.status == ROW_STATUS.ORIGIN ? ROW_STATUS.EDIT : row.status;

            Object.keys(row.values).forEach(key => {
                row.values[key] = {
                    display: null,
                    value: null,
                    scale: -1,
                    ...row.values[key],
                };
            });

            row = {
                ...currentRow,
                ...row,
                values: {
                    ...currentRow.values,
                    ...row.values,
                },
            };
        } else if (isAddStatus) {
            row.status = ROW_STATUS.ADD;

            let colKeys = store.getVisibleLeafColumnKeys();
            colKeys.forEach(colKey => {
                let { initialvalue, itemtype } = store.getColumnProps(colKey);

                if (itemtype === 'refer') {
                    initialvalue = getFxExpression(initialvalue || {}, this.app_context);
                }

                row.values[colKey] = {
                    ...initialvalue,
                    ...row.values[colKey],
                };
            });
        }

        return row;
    });

    if (isDel) {
        data.rows = data.rows.concat(deletedRow);
    }

    execWithShare.call(this, id, shareId => {
        let shareStore = getStore.call(this, shareId);
        shareStore.setRows(data);
    });

    let finalRows = store.getRows();

    let checkedKeys = store.getCheckedKeys(),
        disabledKeys = store.getDisabledKeys(),
        { allpks = [] } = store.getAllRowsInfo(),
        checkedAll = finalRows.every(row => checkedKeys.has(row.key));

    return {
        rows: store.getRows(),
        checkInfo: {
            checkedAll,
            checkedAllpage: allpks.every(row => checkedKeys.has(row.key)),
            crossPageSelectIndex: store.getCheckedKeys(),
            disabledAll: finalRows.every(row => disabledKeys.has(row.key)),
            indeterminate: !checkedAll,
            selectIndex: store.getCheckedKeys(),
        },
    };
}

/**
 * 移动行的方法
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {DataIndex} indexStart
 * @param {DataIndex} indexEnd
 */
export function moveRow(id, indexStart, indexEnd) {
    execWithShare.call(this, id, tabId => {
        let store = getStore.call(this, tabId);
        if (!store) { return; }

        store.moveRow(indexStart, indexEnd);
    });
}

/**
 * 通过Index设置行的编辑性
 * @memberof CardTable
 * @param {{tableId: ModuleId, index: DataIndex, flag: boolean}} settings
 */
export function setRowEditByIndex({ tableId: id, index: dataIndexes, flag }) {
    let store = getStore.call(this, id),
        rows = store.getRows();

    !Array.isArray(dataIndexes) && (dataIndexes = [dataIndexes]);

    dataIndexes.forEach(dataIndex => store.setRowProps(rows[dataIndex].key, { disabled: !flag }));
}

/**
 * 设置侧拉编辑性 [会改模板]
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {ColKey[]} colKeys
 * @param {boolean} enable
 */
export function setModelEdit(id, colKeys, enable) {
    let store = getStore.call(this, id),
        meta = store?.getMeta();

    typeof colKeys === 'string' && (colKeys = [colKeys]);

    if (!store || !meta?.gridrelation?.[id] || Array.isArray(colKeys)) { return; }

    let destEditAreaCode = meta.gridrelation[id].destEditAreaCode;

    Array.isArray(destEditAreaCode) && destEditAreaCode.forEach(destBrowseAreaCode =>
        colKeys.forEach(colKey => { // 抄以前的
            let items = meta[destBrowseAreaCode] && meta[destBrowseAreaCode].items;
            if (!Array.isArray(items)) { return; }

            let itemIndex = items.findIndex(ele => ele.attrcode === colKey);
            if (itemIndex === -1) { return; }

            items[itemIndex] = {
                ...items[itemIndex],
                disabled: !enable,
            };
        }),
    );

    store.setMeta(meta);
}

/**
 * 批量设置列的显示性
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {{hideKeys: ColKey[], showKeys: ColKey[]}} data
 */
export function setColVisibleByKey(id, data) {
    let hideKeys = data?.hideKeys,
        showKeys = data?.showKeys;
    if (!Array.isArray(hideKeys) && !Array.isArray(showKeys)) { return; }

    let store = getStore.call(this, id),
        sideStore = getSideFormStore.call(this, id),
        rows = store.getRows();

    if (Array.isArray(hideKeys)) {
        store.setColumnProps(hideKeys, { visible: false }, { shouldUpdate: false });
        hideKeys.forEach(colKey => {
            sideStore?.setItemAttribute?.(colKey, 'visible', false);
        });
    }

    if (Array.isArray(showKeys)) {
        store.setColumnProps(showKeys, { visible: true }, { shouldUpdate: false });
        showKeys.forEach(colKey => {
            sideStore?.setItemAttribute?.(colKey, 'visible', true);
        });
    }

    rows.forEach(row => {
        Array.isArray(hideKeys) && hideKeys.forEach(colKey => store.setCellProps(row.key, colKey, { visible: false }));
        Array.isArray(showKeys) && showKeys.forEach(colKey => store.setCellProps(row.key, colKey, { visible: true }));
    });

    store.forceUpdate();
}

/**
 * 批量修改某列数据
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {Array<{key: ColKey, data: CellValue}>} colValueArray
 */
export function setColsValue(id, colValueArray) {
    let store = getStore.call(this, id),
        rows = store.getRows();

    if (!rows.length) { return; }

    rows = rows.map(row => {
        let values = {};
        Array.isArray(colValueArray) && colValueArray.forEach(colInfo => {
            let { key: colKey, data: value = {} } = colInfo,
                cellValue = store.getCellValue(row.key, colKey);

            value.hasOwnProperty('display') && (cellValue = { ...cellValue, display: value.display });
            value.hasOwnProperty('value') && (cellValue = { ...cellValue, value: value.value });
            value.hasOwnProperty('scale') && store.setCellProps(colKey, { scale: value.scale });

            values[colKey] = cellValue;
        });

        return {
            ...row,
            status: row.status === ROW_STATUS.ORIGIN ? ROW_STATUS.EDIT : row.status,
            values: {
                ...row.valuse,
                ...values,
            },
        };
    });

    execWithShare.call(this, id, tabId => {
        let tabStore = getStore.call(this, tabId);

        if (!tabStore) { return; }

        tabStore.setRowData(rows);
        updateFormData.call(this, tabId, rows);
    });

    store.forceUpdate();
}

/**
 * 打开侧拉
 * @memberof CardTable
 * @param {ModuleId} tableId
 * @param {'edit' | 'add' | 'browse'} type
 * @param {TableRow} record
 * @param {DataIndex} dataIndex
 * @param {function} callback - 2207未实现
 * @param {boolean} autoFocus - 2207未实现
 */
export function openModel(tableId, type = 'browse', record, dataIndex, callback, autoFocus = false) {
    let relation = getRelation.call(this, tableId),
        mainId = relation.main[tableId],
        mainStore = getStore.call(this, mainId),
        formId = relation.side[tableId];

    if (!formId) {
        console.warn('侧拉formId为空');
        return;
    }

    if (type === 'add') {
        !dataIndex && (dataIndex = getStore.call(this, tableId).getVisibleRows().length);
        execWithShare.call(this, tableId, id => {
            addRow.call(this, id, dataIndex);
        });
        record = getStore.call(this, tableId).getRows()[dataIndex];
    }

    mainStore.openCardTableModal(tableId, formId, dataIndex, this);

    let formStore = getSideFormStore.call(this, tableId);

    formStore?.setStatus(type); // setStatus会调用forceUpdate
}

/**
 * 关闭侧拉
 * @memberof CardTable
 * @param {ModuleId} id
 */
export function closeModel(tableId) {
    let relation = getRelation.call(this, tableId),
        mainId = relation.main[tableId],
        mainStore = getStore.call(this, mainId);

    mainStore.closeCardTableModal();
}

/**
 * 隐藏列
 * @memberof CardTable
 * @param {ModuleId} id - 表格id或者表单id(侧拉表单和展开表单)
 * @param {string | ColKey[]} key
 */
export function hideColByKey(id, keys) {
    let store = getStore.call(this, id);

    if (typeof keys !== 'string' && !Array.isArray(keys)) { return; }

    !Array.isArray(keys) && (keys = [keys]);

    if (store) {
        let rows = store.getRows();
        store.setColumnProps(keys, { visible: false }, { shouldUpdate: false });
        rows.forEach(row => keys.forEach(colKey => store.setCellProps(row.key, colKey, { visible: false })));
        store.forceUpdate();
    } else if (this.formDataSource[id]) {
        keys.forEach(key => {
            this.formDataSource[id]?.setItemAttribute(key, 'visible', false);
        });
    } else if (this.state.meta[id]) {
        this.state.meta[id].items.forEach(col => {
            if (keys.includes(col.attrcode) && col.visible) {
                col.visible = false;
            }
        });
        this.meta.setMeta(this.state.meta);
    }

}

/**
 * 设置列属性
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {MetaItem} newColumn
 * @param {boolean} isShouldUpdate - 2207未实现
 */
export function setColumn(id, newColumn, isShouldUpdate) {
    let store = getStore.call(this, id),
        rows = store.getRows(),
        colKeys = Object.keys(newColumn);

    colKeys.forEach(colKey => store.setColumnProps(colKey, newColumn[colKey]));
    rows.forEach(row => colKeys.forEach(colKey => store.setCellProps(row.key, colKey, newColumn[colKey])));
}

/**
 * 显示列
 * @memberof CardTable
 * @param {ModuleId} id - 表格id或者表单id(侧拉表单和展开表单)
 * @param {string | ColKey[]} key
 */
export function showColByKey(id, keys) {
    let store = getStore.call(this, id);

    if (typeof keys !== 'string' && !Array.isArray(keys)) { return; }

    !Array.isArray(keys) && (keys = [keys]);

    if (store) {
        let rows = store.getRows();
        store.setColumnProps(keys, { visible: true }, { shouldUpdate: false });
        rows.forEach(row => keys.forEach(colKey => store.setCellProps(row.key, colKey, { visible: true })));
        store.forceUpdate();
    } else if (this.formDataSource[id]) {
        keys.forEach(key => {
            this.formDataSource[id]?.setItemAttribute(key, 'visible', true);
        });
    } else if (this.state.meta[id]) {
        this.state.meta[id].items.forEach(col => {
            if (keys.includes(col.attrcode) && (typeof col.visible == 'undefined' || col.visible == false)) {
                col.visible = true;
            }
        });
        this.meta.setMeta(this.state.meta);
    }
}

/**
 * 获取表格某列数据
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {ColKey} key
 * @param {boolean} flag - 2207未实现 多表头标识，默认false
 * @param {boolean} isDel - 是否获得删除行的数据
 */
export function getColValue(id, colKey, flag = false, isDel = true) {
    let store = getStore.call(this, id),
        rows = isDel ? store.getRows() : store.getVisibleRows();

    return rows.map(row => store.getCellValue(row.key, colKey));
}

/**
 * 设置表格某列为同一个数据
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {ColKey} key
 * @param {CellValue} value
 */
export function setColValue(id, colKey, value) {
    execWithShare.call(this, id, tabId => {
        let store = getStore.call(this, tabId),
            rows = store.getRows();

        rows.forEach(row => {
            if (row.status === ROW_STATUS.DELETE) { return; }

            row.status == ROW_STATUS.ORIGIN && (row.status = ROW_STATUS.EDIT);

            let cellValue = store.getCellValue(row.key, colKey) || {};

            value.hasOwnProperty('scale') && (cellValue.scale = value.scale);
            value.hasOwnProperty('display') && (cellValue.display = value.display);

            if (value.hasOwnProperty('value')) {
                cellValue.value = value.value;
                // saveChangedRowsOldValue.call(this, id, row.key, colKey, value.value); // 暂不存旧值
            }

            store.setCellValue(row.key, colKey, { value: cellValue, shouldUpdate: false });

            updateFormItemData.call(this, tabId, row.dataIndex, colKey, cellValue);
        });

        store.forceUpdate();
    });
}

/**
 * 最大化多表中表体卡片列表
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {boolean} open
 * @param {ModuleId[]} expandedRowKeys - 2207未实现
 */
export function openListView(id, open, expandedRowKeys) {
    let store = getStore.call(this, id);
    store.setExpandAllStatus(open);
}

/**
 * 最大化多表中表格
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {boolean} open
 */
export function openMaxTable(id, open) {
    execWithHome.call(this, id, moduleId => {
        let store = getStore.call(this, moduleId);
        store.setMaxStatus(open);
    });
}

/**
 * 获取表格的最大化状态
 * @memberof CardTable
 * @param {ModuleId} id
 * @returns {boolean}
 */
export function isMaxView(id) {
    let store = getStore.call(this, id);
    return store.getMaxStatus();
}

/**
  根据ID获取模板数据
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {ColKey} colKey
 * @param {boolean} flag - 2207未实现，多表头标识，默认false
 * @returns {MetaItem}
 */
export function getMetaValByKey(id, colKey, flag = false) {
    let store = getStore.call(this, id);
    return store.getVisibleColumns().filter(col => col.key === colKey)?.[0];
}

/**
  设置列编辑性
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {ColKey | ColKey[]} colKeys
 * @param {boolean} disabled - 禁用,默认true
 */
export function setColEditableByKey(id, colKeys, disabled = true) {
    let store = getStore.call(this, id),
        rows = store.getRows();

    !Array.isArray(colKeys) && (colKeys = [colKeys]);

    colKeys.forEach(colKey => store.setColumnProps(colKey, { disabled }, { shouldUpdate: false }));
    rows.forEach(row => colKeys.forEach(colKey => store.setCellProps(row.key, colKey, { disabled })));

    store.forceUpdate();
}

/**
 * 根据index获取侧拉数据
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {DataIndex} dataIndex
 * @returns {Object<ColKey, { display, value }>}
 */
export function getModalDataByIndex(id, dataIndex) {
    let relation = getRelation.call(this, id);

    if (relation?.side[id]) {
        let formStore = this.formDataSource[relation.side[id]],
            tableStore = getStore.call(this, id),
            items = formStore.getMetaItems(relation.side[id]),
            row = tableStore.getRows()[dataIndex];

        return items.reduce((values, item) => {
            values[item.attrcode] = row.values[item.attrcode];
            return values;
        }, {});
    } else {
        return {};
    }
}

/**
 * 设置侧拉某控件显隐
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {ColKey} colKey
 * @param {boolean} enable
 */
export function showEditAreaKey(id, colKey, enable = false) {
    let formStore = getSideFormStore.call(this, id);
    !Array.isArray(colKey) && (colKey = [colKey]);

    colKey.forEach(key => {
        formStore?.setItemAttribute(key, 'visible', enable); // TODO 改成表单一次性设置多个值
    });
}

/**
 * 控制主表的收起展开
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {boolean} isExpand
 */
export function toggleCardTable(ids, isExpand = false) {
    !Array.isArray(ids) && (ids = [ids]);

    ids.forEach(id => {
        let store = getStore.call(this, id),
            { onHeadAngleToggle } = store?.getConfig() || {};

        execWithHome.call(this, id, moduleId => {
            let homeStore = getStore.call(this, moduleId),
                oldExpand = homeStore?.getTableExpand();

            if (!homeStore) { return; }
            homeStore?.setTableExpand(isExpand);
            oldExpand !== isExpand && onHeadAngleToggle?.({ ...this.props, ...this.output }, id, isExpand, homeStore.getActiveTab() || moduleId);
        });
    });
}

/**
   添加参照过滤属性，表体和侧拉均生效
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {{ColKey: function | object}} settings
 * @param {boolean} shouldForceUpdate - 2207未实现
 */
export function setQueryCondition(id, settings, shouldForceUpdate = true) {
    execWithShare.call(this, id, shareId => {
        let store = getStore.call(this, shareId);

        if (!store) { return; }

        Object.keys(settings).forEach(key =>
            store.setColumnProps(key, { queryCondition: settings[key] }),
        );
    });

    let formStore = getSideFormStore.call(this, id);

    formStore && Object.keys(settings).forEach(key => {
        formStore?.setItemAttribute(key, 'queryCondition', settings[key]);
    });

}

//---------------- 孙表API ----------------//
/**
 *
 */
/**
 * 设置孙表数据
 * @memberof CardTable
 * @param {{rowid: RowKey, parentId: ModuleId, tableId: ModuleId, data: TableData, shouldForceUpdate: boolean, callback: function, isCache: boolean, isTop: boolean, isDiffUpdate: boolean}} settings
 */
export function setGrandTableData({ rowid, parentId, tableId: grandId, data, shouldForceUpdate, callback, isCache = true, isTop = false, isDiffUpdate = false }) {
    let parentStore = getStore.call(this, parentId);
    if (isDiffUpdate) {
        // 这里是抄的以前的，没做性能优化
        let oldRows = parentStore.getGrandRows(false)?.[rowid]?.[grandId]?.rows || [];
        data.rows = data.rows.map(row => {
            // 若该行数据，旧值中已存在，则合并新行和旧行
            if (row.rowid) {
                let index = oldRows.findIndex(e => e.rowid === row.rowid);
                if (index !== -1) {
                    row.values = {
                        ...oldRows[index].values,
                        ...row.values,
                    };
                }
                //否则为新增行，生成rowid
            } else {
                row.rowid = generateKey();
            }
            return row;
        });
    } else {
        data.rows = data.rows.map(item => {
            item.rowid = item.rowid || generateKey();
            return item;
        });
    }
    parentStore.setGrandRows(rowid, grandId, data);
    let start = new Date().getTime();
    isCache && parentStore.setCache(['grandTableData', rowid, grandId], JSON.parse(JSON.stringify(data)));
    console.log(new Date().getTime() - start);
    callback?.(this.props, grandId);
}

/**
 * 获取孙表数据
 * @memberof CardTable
 * @param {{parentId: ModuleId, isDeepCopy: boolean}} settings - isDeepCopy未实现
 * @returns {{RowKey: { ModuleId: TableRow[] }}}
 */
export function getAllGrandData({ parentId, isDeepCopy }) {
    let parentStore = getStore.call(this, parentId);

    return parentStore.getGrandRows();
}

/**
 * 获取孙表数据 TODO::处理view
 * @memberof CardTable
 * @param {{parentId: ModuleId, view: string}} settings
 * @returns {{RowKey: { ModuleId: TableRow[] }}} - view未实现
 */
export function getViewGrandData({ parentId, view }) {
    let parentStore = getStore.call(this, parentId);

    return parentStore.getGrandRows();
}

/**
 * 删除孙表数据
 * @memberof CardTable
 * @param {{parentId: ModuleId, rowidList: RowKey[], delAll: boolean}} settings - delAll为true时删除所有孙表数据,为false时删除rowidList指定行的孙表数据
 */
export function delCacheGrandData({ parentId, rowidList = [], delAll = false }) {
    let parentStore = getStore.call(this, parentId);
    if (delAll) {
        parentStore.clearGrandRows();
    } else {
        rowidList.forEach(rowKey => {
            parentStore.deleteGrandRows(rowKey);
        });
    }
}

/**
 * 查找指定pkcode值的行ID,pkcode为创建表格时的配置项
 * @memberof CardTable
 * @param {{ parentId: ModuleId, plcodeValue: string | boolean }} settings
 * @returns {RowKey}
 */
export function getRowIdFromPkcode({ parentId, plcodeValue }) {
    let store = getStore.call(this, parentId),
        { pkcode } = store?.getConfig() || {},
        rows = store.getRows(),
        rowid = rows.find(row => row.values[pkcode] && row.values[pkcode].value === plcodeValue)?.key;
    return rowid || '';
}

/**
 * 设置筛选条件
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {{attrcode: ColKey, rowId: RowKey, value: string}} settings
 */
export function saveFilterCustomColData(id, { attrcode, rowId, value }) {
    let store = getStore.call(this, id),
        info = store.getFilterInfo();
    store.setFilterInfo({ ...info, conditions: [{ key: attrcode, value }] });
}

/**
 * 获取侧拉数据index
 * @memberof CardTable
 * @param {ModuleId} id
 * @returns {DataIndex}
 */
export function getSideParentCurrentIndex(id) {
    let relation = getRelation.call(this, id);
    return getStore.call(this, relation.main[id])?.getCardTableModalInfo()?.dataIndex ?? -1;
}

/**
 * 获取侧拉数据index
 * @memberof CardTable
 * @param {ModuleId} id
 * @returns {Rowid}
 */
export function getSideRowid(id) {
    let relation = getRelation.call(this, id);
    return getStore.call(this, relation.main[id])?.getCardTableModalInfo()?.rowKey ?? '';
}

/**
 * 更新表格 [在业务显示调用了阻止更新的方法并且恢复更新后，调用本方法即可刷新表格]
 * @memberof CardTable
 * @param {ModuleId| ModuleId[]} tableIds
 * @param {boolean} autoFocus - 2207未实现
 */
export function updateTable(tableIds, autoFocus) {
    tableIds = Array.isArray(tableIds) ? tableIds : [tableIds];

    tableIds.forEach(tableId => {
        let store = getStore.call(this, tableId);
        if (!store) {
            return;
        }

        store.forceUpdate();
    });
}

/**
 * @memberof CardTable
 * 遗留的方法，未知功能
 */
export const updateAfterBatchChange = createUpdateAfterBatchChange({ getStore, updateAndAddRows });

/**
 * 更新数据，不建议使用
 * @memberof CardTable
 * @param {{rows: TableRow[], tableId: ModuleId}} settings
 * @returns {array} - 返回空数组
 */
export function updateAndAddRows({ rows, tableId }) {
    if (!Array.isArray(rows)) { return; } // 防止方法报错导致preventUpdate状态未解除
    execWithShare.call(this, tableId, tabId => {
        let tabStore = getStore.call(this, tabId);

        if (!tabStore) { return; }

        !this.isUpdatePage && tabStore.getInstance().preventUpdate(true);

        let addRows = [], updateRows = [], allRows = tabStore.getRows();
        rows.forEach(row => {
            let rowKey = row.key || row.rowId || row.rowid || generateKey();

            let oldRow = tabStore.getRowData(rowKey);
            if (oldRow) {
                // 更新行
                updateRows.push({
                    ...oldRow,
                    ...row,
                    status: (!row.status || row.status === ROW_STATUS.ORIGIN) ? ROW_STATUS.EDIT : row.status,
                });
            } else {
                // 增行
                addRows.push({ ...row, key: rowKey });
            }
        });

        tabStore.setRowData(updateRows);
        tabStore.addRow(allRows.length, addRows, { autoFocus: false });

        updateFormData.call(this, tabId, allRows);

        !this.isUpdatePage && tabStore.getInstance().preventUpdate(false);
        tabStore.forceUpdate();
    });

    return [];
}

/**
 * 更新数据
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {ColKey} pkname
 * @param {TableRow[]} nextRows
 * @param {string} sagaErrorMessage
 */
export function updateDataByRefresh(id, pkname, nextRows, sagaErrorMessage) {
    let store = getStore.call(this, id),
        rows = store.getRows();

    if (!rows?.length || !Array.isArray(nextRows)) { return; }

    let groupedRowsByKey = rows.reduce((group, row) => {
        group[row.values[pkname].value] = row;
        return group;
    }, {});

    nextRows = nextRows.map(row => {
        let rawData = groupedRowsByKey[row[pkname]];

        return {
            ...rawData,
            values: {
                ...rawData.values,
                ...Object.entries(row).reduce((values, [key, value]) => {
                    values[key] = { value };
                    return values;
                }, {}),
                saga_errormesg: row.saga_status === '1' ? sagaErrorMessage : '',
            },
        };
    });
    store.setRowData(nextRows);

    execWithShare.call(this, id, tabId => {
        let tabStore = getStore.call(this, tabId);

        if (!tabStore) { return; }

        tabStore.setRowData(nextRows);
        updateFormData.call(this, tabId, tabStore.getRows());
    });
}

/**
 * 已废弃
 * @memberof CardTable
 * @param {ModuleId} id
 */
export function refresh(id) { }

/**
 * 检查表格必输项是否为空(标准节点保存使用)
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {boolean} autoFocus
 * @returns {{ requiredItems: object, regExpItem: Array, maxItems: Array, content: string, passed: boolean}}
 */
export function checkTabsTableToSave(id, autoFocus = true, customRules) {
    let requiredInfo = {},
        toastMsg = '',
        { containerrelation } = this.state.meta;

    let containerIds = containerrelation?.[id] ? [].concat(...containerrelation?.[id]) : [id],
        firstErrorTab,
        firstErrorCell;

    containerIds.forEach(tabId => {
        requiredInfo[tabId] = {};

        execWithShare.call(this, tabId, tableId => {
            let tableStore = getStore.call(this, tableId),
                errInfo = tableStore?.validateToSave(customRules),
                tableName = tableStore?.getMeta()?.[tableId].name;
            let {
                regErrInfo,
                maxErrInfo,
                requiredErrInfo,
                customerErrInfo,
            } = errInfo || {};
            let hasError = regErrInfo.size || maxErrInfo.size || requiredErrInfo.size || customerErrInfo.size;
            if (!tableStore) { return; }

            let locale = tableStore.getLocale();
            requiredInfo[tabId][tableId] = {};

            if (!hasError) { return; }

            if (!firstErrorTab) {
                firstErrorTab = tableId;
                if (errInfo?.firstErrorCell?.length) {
                    firstErrorCell = errInfo.firstErrorCell;
                } else {
                    let [rowKey, colKeys] = [...requiredErrInfo][0],
                        colKey = [...colKeys][0];
                    firstErrorCell = [rowKey, colKey];
                }
            }
            let generateToastMsg = (rowKey, colKeys) => {
                let row = tableStore.getRowData(rowKey),
                    errLabels = [];
                requiredInfo[tabId][tableId][rowKey] = {};

                [...colKeys].forEach(colKey => {
                    errLabels.push(`[${tableStore.getColumnProps(colKey, 'label')}]`);
                    requiredInfo[tabId][tableId][rowKey][colKey] = colKey;
                });
                toastMsg = toastMsg.concat(`${locale['table_first'] /*第*/}${row.dataIndex + 1}${locale['table_row'] /*行*/}`).concat(errLabels.join('、')).concat('；\n');
            };
            if (errInfo.customerErrInfo.size) {
                toastMsg = toastMsg.concat(`${locale['table_no_empty'] /*以下字段不能为空*/}\n`.concat(tableName).concat(`(${locale['table008'] /*页签*/})：\n`));
                errInfo.customerErrInfo.forEach((colKeys, rowKey) => {
                    generateToastMsg(rowKey, colKeys);
                });
            }
            if (errInfo.requiredErrInfo.size) {
                toastMsg = toastMsg.concat(`${locale['table_no_empty'] /*以下字段值不能为空*/}\n`.concat(tableName).concat(`(${locale['table008'] /*页签*/})：\n`));
                errInfo.requiredErrInfo.forEach((colKeys, rowKey) => {
                    generateToastMsg(rowKey, colKeys);
                });
            }
            if (errInfo.regErrInfo.size) {
                toastMsg = toastMsg.concat(`${locale['table_illegal'] /*以下字段不合法*/} :\n`.concat(tableName).concat(`(${locale['table008'] /*页签*/})：\n`));
                errInfo.regErrInfo.forEach((colKeys, rowKey) => {
                    generateToastMsg(rowKey, colKeys);
                });
            }
            if (errInfo.maxErrInfo.size) {
                toastMsg = toastMsg.concat(`${locale['table_over_long'] /*以下字段长度超长*/} :\n`.concat(tableName).concat(`(${locale['table008'] /*页签*/})：\n`));
                errInfo.maxErrInfo.forEach((colKeys, rowKey) => {
                    generateToastMsg(rowKey, colKeys);
                });
            }
        });
    });

    if (autoFocus && firstErrorTab && firstErrorCell) {
        // 切换到第一个错误页签
        execWithHome.call(this, firstErrorTab, moduleId => {
            getStore.call(this, moduleId).setActiveTab(firstErrorTab);
        });

        // 等页签切完再聚焦
        setTimeout(() => {
            // 聚焦到第一个错误单元格
            getStore.call(this, firstErrorTab).focusOnCell(...firstErrorCell);
        }, 50);
    }

    return {
        requiredItems: requiredInfo,
        regExpItem: [],
        maxItems: [],
        content: toastMsg,
        passed: toastMsg.length === 0,
    };
}

/**
 * 检查表格必输项是否为空
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {boolean} autoFocus
 * @returns {{ requiredItems: object, regExpItem: Array, maxItems: Array, content: string, passed: boolean}}
 */
export function checkTabsTableRequired(id, autoFocus = true, customRules) {
    let requiredInfo = {},
        toastMsg = '',
        tabTableTitle = '',
        tabTableDetailTitle = {},
        requiredItemsKeyVal = [],
        tabNameRequiredItems = [],
        tableName = '',
        { containerrelation } = this.state.meta;

    let containerIds = containerrelation?.[id] ? [].concat(...containerrelation?.[id]) : [id],
        firstErrorTab,
        firstErrorCell;

    containerIds.forEach(tabId => {
        requiredInfo[tabId] = {};

        execWithShare.call(this, tabId, tableId => {
            let tableStore = getStore.call(this, tableId),
                errInfo = tableStore?.validate(customRules);
            tableName = tableStore?.getMeta()?.[tableId].name;

            if (!tableStore) { return; }

            let locale = tableStore.getLocale();
            requiredInfo[tabId][tableId] = {};

            if (!errInfo.size) { return; }

            if (!firstErrorTab) {
                let [rowKey, colKeys] = [...errInfo][0],
                    colKey = [...colKeys][0];

                firstErrorTab = tableId;
                firstErrorCell = [rowKey, colKey];
            }
            tabTableTitle = `${locale['table_no_empty'] /*以下字段值不能为空*/}\n`;
            toastMsg = `${locale['table_no_empty'] /*以下字段值不能为空*/}\n`.concat(tableName).concat(`(${locale['table008'] /*页签*/})：\n`);

            errInfo.forEach((colKeys, rowKey) => {
                let row = tableStore.getRowData(rowKey),
                    errLabels = [],
                    errModel = [];
                requiredInfo[tabId][tableId][rowKey] = {};
                requiredItemsKeyVal = [];
                tableName = tableStore?.getMeta()?.[tableId].name;

                [...colKeys].forEach((colKey, index) => {
                    errLabels.push(`${tableStore.getColumnProps(colKey, 'label')}`);
                    requiredInfo[tabId][tableId][rowKey][colKey] = colKey;
                    requiredItemsKeyVal.push({ colKey: colKey, rowKey: rowKey, display: `${tableStore.getColumnProps(colKey, 'label')}`, area: tableId, tableName: tableName, rowIndex: row.dataIndex + 1 });
                });

                errLabels.forEach((label, index) => {
                    errModel.push('${field' + (index + 1) + '}');
                    // tabTableDetailTitle = (`${locale['table_first']/*第*/}${row.dataIndex + 1}${locale['table_row']/*行*/}`).concat(errModel.join('、'));
                });
                let txt = (`${locale['table_first']/*第*/}${row.dataIndex + 1}${locale['table_row']/*行*/}`).concat(errModel.join('、'));
                tabTableDetailTitle = Object.assign(tabTableDetailTitle, { [rowKey]: txt });
                let targetIndex = tabNameRequiredItems.findIndex(item => { return item.name === tableName; });
                // let targetItem=tabNameRequiredItems.findIndex((item) => { return item.name === tableName })
                if (targetIndex !== -1) {
                    tabNameRequiredItems[targetIndex].detail.push(...requiredItemsKeyVal);
                } else {
                    tabNameRequiredItems.push({ name: tableName, detail: requiredItemsKeyVal });
                }
                console.log(tabNameRequiredItems);
            });

        });
    });

    if (autoFocus && firstErrorTab && firstErrorCell) {
        // 切换到第一个错误页签
        execWithHome.call(this, firstErrorTab, moduleId => {
            getStore.call(this, moduleId).setActiveTab(firstErrorTab);
        });

        // 等页签切完再聚焦
        setTimeout(() => {
            // 聚焦到第一个错误单元格
            getStore.call(this, firstErrorTab).focusOnCell(...firstErrorCell);
        }, 50);
    }

    return {
        requiredItems: requiredInfo,
        regExpItem: [],
        maxItems: [],
        content: toastMsg,
        tabTableTitle: tabTableTitle,
        tabTableDetailTitle: tabTableDetailTitle,
        requiredItemsKeyVal: requiredItemsKeyVal,
        tabNameRequiredItems: tabNameRequiredItems,
        passed: !toastMsg,
        tableName,
    };
}
/**
 * 设置模板上的列属性
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {{ColKey: ColumnProps}} data
 * @param {boolean} isShouldUpdate
 */
export function setRelationedColumn(id, data, isShouldUpdate = true) { //TODO setFormItemsAttribute用这个 edit同步改一下
    let store = getStore.call(this, id),
        colKeys = Object.keys(data),
        rows = store?.getRows();

    if (!store || typeof data !== 'object') { return; }

    colKeys.forEach(colKey => store.setColumnProps(colKey, data[colKey], { shouldUpdate: false }));
    rows.forEach(row => colKeys.forEach(colKey => store.setCellProps(row.key, colKey, data[colKey])));

    // 抄原来的
    let meta = store.getMeta(),
        destBrowseAreaCode = meta.gridrelation?.[id]?.destBrowseAreaCode,
        destEditAreaCode = meta.gridrelation[id].destEditAreaCode;

    Object.keys(data).forEach(colKey => {
        if (destBrowseAreaCode) {
            if (!Array.isArray(meta[destBrowseAreaCode]?.items)) { return; }

            let changeCol = meta[destBrowseAreaCode].items.find(e => e.attrcode === colKey);
            changeCol[colKey] = data[colKey];
        }

        if (!Array.isArray(destEditAreaCode) || !destEditAreaCode.length) { return; }

        destEditAreaCode.forEach(sideId => {
            if (!Array.isArray(meta[sideId]?.items)) { return; }

            let changeCol = meta[sideId].items.find(e => e.attrcode === colKey);
            changeCol[colKey] = data[colKey];

        });
    });

    store.forceUpdate();
}

/**
 * 更新合计行
 * @memberof CardTable
 * @param {ModuleId} moduleId
 */
export function updateTotal(moduleId) {
    execWithShare.call(this, moduleId, tableId => {
        let store = getStore.call(this, tableId);
        store.updateTotal();
    });
}

/**
 * 已废弃
 * @memberof CardTable
 */
export function updateTableHeight() { }

/**
 * 用于变换create时传入的config
 */
export function _setConfigTranslator(id, translator) {
    let store = getStore.call(this, id);
    store.setConfigTranslator(translator);
}

/**
 * 滚动到指定位置
 * @memberof CardTable
 * @param {ModuleId} id
 * @param {{ rowKey: RowKey, colKey: ColKey }} config
 */
export function scrollIntoView(id, { rowKey, colKey }) {
    let store = getStore.call(this, id);

    /** @detail 滚动到行 */
    store.scrollIntoView({ rowKey, colKey });
}

/**
 * 获取选中的单元格
 * @memberof CardTable
 * @param {ModuleId} id
 */
export function getSelectedCell(id) {
    let store = getStore.call(this, id);

    /** @detail 滚动到行 */
    return store.getSelectedCell();
}

/**
 * 设置激活的单元格
 * @memberof EditTable
 * @param {ModuleId} moduleId
 * @param {string} rowKey
 * @param {string} colKey
 */
export function setActiveCell(moduleId, rowKey, colKey) {
    let store = getStore.call(this, moduleId);
    store?.setActiveCell({ rowKey, colKey });
}

/**
 * 设置排序条件
 * @memberof SimpleTable
 * @param {ModuleId} id
 * @param {{ logic: 'or' | 'and', conditions: Array<{ key: ColKey, value, string, logic: 'equal' | 'greaterThan' | 'lessThan' | 'notIncluded' | 'included' }> }} record
 */
export function setSortInfo(id, info, { shouldUpdateRows = true } = {}) {
    let store = getStore.call(this, id);
    store?.setSortInfo(info, { shouldUpdateRows });
}

/**
 * 获取排序条件
 * @memberof SimpleTable
 * @param {ModuleId} id
 */
export function getSortInfo(id) {
    let store = getStore.call(this, id);
    return store?.getSortInfo();
}

/**
 * =================================== 工具方法 ===================================
 */

// 获取cardDataSource上的store store是平铺的
export function getStore(id) {
    if (this.cardTableDataSource[id]) {
        return this.cardTableDataSource[id];
    } else {
        return setStore.call(this, id);
    }
}

function setStore(id) {
    this.cardTableDataSource[id] = CardTable.useCardTable();
    return this.cardTableDataSource[id];
}

// 获取this上的relation 存了各种关联关系
function getRelation(id) {
    return getStore.call(this, id).getRelation();
}

function execWithDependence(id, callback) {
    let relation = getRelation.call(this, id);
    if (!relation) { return; }

    if (Array.isArray(relation.dependence)) {
        relation.dependence.forEach(tabIds => {
            callback(tabIds);
        });
    }
}

function execWithTables(id, callback) {
    let relation = getRelation.call(this, id);
    if (!relation) { return; }

    if (Array.isArray(relation.tables)) {
        relation.tables.forEach(tabIds => {
            callback(tabIds);
        });
    }
}

function execWithShare(id, callback) {
    let relation = getRelation.call(this, id);

    // 想要设置share，必须先setMeta。
    // 为了兼容未setMeta的情况，有了下面的分支，但只能设置一个页签的数据
    // 如果想同步单来源，必须先setMeta
    if (Array.isArray(relation?.share?.[id])) {
        relation.share[id].forEach(tabIds => {
            getShareCardIds.call(this, tabIds).forEach(shareId => callback(shareId));
        });
    } else {
        getShareCardIds.call(this, id).forEach(shareId => callback(shareId));
    }

}

function getShareCardIds(id) {
    let tabStore = getStore.call(this, id),
        shareIds = new Set([id]);
    if (tabStore.getGridTableId()) { // 入参id是表格id，看看是不是卡片表格的
        shareIds.add(tabStore.getGridTableId());
    } else if (tabStore.getConfig()?.tableId) { // 入参是卡片表格的id
        shareIds.add(tabStore.getConfig().tableId);
    }
    return [...shareIds];
}

function execWithHome(id, callback) {
    let relation = getRelation.call(this, id);
    if (!relation) { return; }

    if (relation.home[id]) {
        callback(relation.home[id]);
    } else {
        callback(id);
    }
}

// TODO:grand里只有table，不是dependence
function execWithGrands(id, callback) {
    let relation = getRelation.call(this, id);
    if (!relation) { return; }

    if (Array.isArray(relation.grand[id])) {
        relation.grand[id].forEach(tabIds => {
            callback(tabIds);
        });
    } else {
        callback(id);
    }
}

function getSideFormStore(tableId) {
    let relation = getRelation.call(this, tableId),
        formId = relation?.side[tableId];

    if (!formId) {
        return;
    }

    return this.formDataSource[formId];
}

// 一次性更新多条展开、侧拉表单数据
function updateFormData(id, rows) {
    !Array.isArray(rows) && (rows = [rows]);

    rows = rows.filter(row => row.status !== ROW_STATUS.DELETE);

    let relation = getRelation.call(this, id);

    if (!relation?.main?.[id]) {
        return;
    }

    let mainId = relation.main[id],
        mainStore = getStore.call(this, mainId),
        tableStore = getStore.call(this, id),
        { dataIndex, open, tableId } = mainStore.getCardTableModalInfo() || {},
        currentOpenKey = tableStore.getRows()[dataIndex]?.key;

    if (tableId !== id || !open) { return; } // 在循环里调用本方法，只有在已经展开的tableId里才需要更新数据

    for (let i = 0; i < rows.length; i++) {
        let row = rows[i];
        let rowKey = row.key || row.rowId || row.rowid;
        if (currentOpenKey === rowKey) {
            // 只有当前打开的表单需要更新
            getSideFormStore.call(this, id)?.setAllFormValue(row.values || {});
            break;
        }
    }

    if (dataIndex > rows.length || rows.length === 0) {
        mainStore.closeCardTableModal(); //close里会更新组件
    } else {
        mainStore.getInstance().updateComponent('CardTableWithModal'); // 刷新按钮状态，避免各种情况导致按钮没刷新
    }
}

// 更新单条展开、侧拉表单数据
function updateFormItemData(tableId, rowDataIndex, colKey, value) {

    let relation = getRelation.call(this, tableId),
        mainId = relation?.main[tableId],
        dataIndex = mainId && getStore.call(this, mainId)?.getCardTableModalInfo()?.dataIndex;

    if (dataIndex === rowDataIndex) {
        getSideFormStore.call(this, tableId)?.setItemValue(colKey, value);
    }
}

function _formatRow(row) {
    if (row) {
        return {
            rowid: row.rowid,
            status: row.status,
            isOptimized: row.isOptimized,
            // selected: checkedKeys.has(row.key || row.rowId),
            values: row.values,
        };
    }
}

export function createRelation(meta, mainId) {
    // 建立table关联关系
    let { containerrelation = {}, gridrelation = {} } = meta;

    let tableRelation = {
        tables: [], // 纵向多主表信息
        tabs: {}, // 横向多页签信息
        home: {}, // 每组页签的第一个tabId
        share: {}, // 单来源页签(需要共享数据的页签)
        dataSource: {}, // 单来源页签(需要共享数据的页签)的第一个页签（所有单来源页签的数据源）
        expand: {}, // 展开行的模板
        expandFrom: {}, // 展开行模板对应的主表id
        side: {}, // cardTable侧拉的Form模板
        sideFrom: {}, // cardTable侧拉Form模板对应的主表id
        grand: {}, // cardTable侧拉的孙表模板（孙表信息在createCardTable时才确定，需要在createCardTable时延迟执行）
        dependence: [], // 所有相关联的id
        main: {}, // 第一个页签id
    };

    if (containerrelation[mainId]) {
        // 多来源
        let multipleRelation = containerrelation[mainId];
        multipleRelation = Array.isArray(multipleRelation) ? multipleRelation : multipleRelation.tabs;

        tableRelation.multiple = true;
        tableRelation.tables = multipleRelation.map(relation => relation[0]);

        tableRelation.tabs = multipleRelation.reduce(
            (tabs, relation) => {
                tabs[relation[0]] = relation
                    .flatMap(moduleId => gridrelation?.[moduleId]?.tabRelation || [moduleId]);

                // 多来源ID
                relation.forEach(tabId => {
                    if (gridrelation[tabId]?.tabRelation?.length) {
                        // 单来源
                        gridrelation[tabId].tabRelation.forEach((subId, index, arr) => {
                            tableRelation.share[subId] = arr;
                            tableRelation.dataSource[subId] = tabId;

                            tableRelation.expand[subId] = gridrelation[tabId].destBrowseAreaCode || '';
                            tableRelation.expandFrom[tableRelation.expand[subId]] = subId;
                            tableRelation.side[subId] = gridrelation[tabId].destEditAreaCode?.length ? gridrelation[tabId].destEditAreaCode[0] : '';
                            tableRelation.sideFrom[tableRelation.side[subId]] = subId;

                            // 创建侧拉的FormStore
                            // TODO:展开的怎么办
                            // TODO:单来源的共享meta吗？
                            let formId = tableRelation.side[subId];
                            if (formId && !this.formDataSource[formId]) {
                                this.use.form(formId);
                            }
                        });
                    } else {
                        // 多来源
                        tableRelation.share[tabId] = [tabId];
                    }
                });

                tabs[relation[0]].forEach(subId => {
                    tableRelation.home[subId] = relation[0];
                });

                return tabs;
            },
            tableRelation.tabs,
        );

        tableRelation.dependence = tableRelation.tables.flatMap(
            moduleId => tableRelation.tabs[moduleId],
        );
    } else if (gridrelation[mainId]) {
        // 单来源
        let singleRelation = gridrelation[mainId];
        if (!singleRelation.tabRelation?.length) {
            singleRelation.tabRelation = [mainId];
        }

        tableRelation.tables = [mainId];
        tableRelation.tabs[mainId] = singleRelation.tabRelation;

        singleRelation.tabRelation.forEach((subId, index, arr) => {
            tableRelation.share[subId] = arr;
            tableRelation.dataSource[subId] = mainId;
            tableRelation.home[subId] = mainId;
            tableRelation.expand[subId] = singleRelation.destBrowseAreaCode || '';
            tableRelation.expandFrom[tableRelation.expand[subId]] = subId;
            tableRelation.side[subId] = singleRelation.destEditAreaCode?.length ? singleRelation.destEditAreaCode[0] : '';
            tableRelation.sideFrom[tableRelation.side[subId]] = subId;

            // 创建侧拉的FormStore
            // TODO:展开的怎么办
            // TODO:单来源的共享meta吗？
            let formId = tableRelation.side[subId];
            if (formId && !this.formDataSource[formId]) {
                this.use.form(formId);
            }
        });

        tableRelation.dependence = singleRelation.tabRelation;
    } else {
        // 单个表格，加默认关联关系
        tableRelation.tables = [mainId];
        tableRelation.tabs[mainId] = [mainId];
        tableRelation.share[mainId] = [mainId];
        tableRelation.dataSource[mainId] = mainId;
        tableRelation.home[mainId] = mainId;
        tableRelation.expand[mainId] = '';
        tableRelation.side[mainId] = '';
        tableRelation.dependence = [mainId];
    }

    tableRelation.dependence.forEach(tableId => {
        if (!(this.cardTableDataSource && this.cardTableDataSource[tableId])) {
            this.use.cardTable(tableId);
        }
        tableRelation.main[tableId] = mainId;
    });

    return tableRelation;
}